/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, Dialog, CircularProgress, Typography, useMediaQuery, useTheme, Switch, FormControlLabel, Tooltip } from "@mui/material";
import DescriptionMatchSettings from "../components/popup/description-match-settings";
import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";

import TransactionsTable, { TransactionRow, TransactionsTableHandle } from "../components/transactions-table-virtual";
import TransactionsTableMobile, { TransactionsTableMobileHandle } from "../components/transactions-table-mobile";
import ChangeProvisionDatePopup from "../components/popup/change-provision-date";
import Topbar from "../components/topbar";
import XeroSyncButton from "../components/xero-sync-button";
import UserMenu from "../components/user-menu";
import { Filters } from "../components/filters";
import { FiltersMobile } from "../components/filters-mobile";
import SidebarMobileDrawer from "../components/sidebar-mobile-drawer";
import { DropdownOption } from "../components/ui/dropdown";
import { Upload, Plus, Receipt, Menu, Calendar } from "lucide-react";
import { useSidebarMobile } from "../providers/sidebar-mobile-context";
import SimulationDetail from "../components/popup/simulation-detail";
import AddProvisionPopup from "../components/popup/add-provision";
import ImportMatchPopup from "../components/popup/import-match";
import BasiqImportPopup from "../components/popup/basiq-import";

import AddEntityPopup from "../components/popup/add-entity";
import SimulationsPopup from "../components/popup/simulations";
import { Provision } from './provisions';
import { FilterParams } from "../components/transactions-table";
import DeletePopup from "../components/popup/delete";
import DateConfirmPopup from "../components/popup/date-confirm";
import transactionImage from '../files/images/transaction.png';
import MountWhenOpened from '../components/mount-when-opened';
import TransactionDetail from "../components/popup/transaction-detail";
import TransactionDetailMobile from "../components/popup/mobile/transaction-detail-mobile";
import DeletePopupMobile from "../components/popup/mobile/delete-mobile";
import DateConfirmPopupMobile from "../components/popup/mobile/date-confirm-mobile";
import ChangeProvisionDatePopupMobile from "../components/popup/mobile/change-provision-date-mobile";
import DescriptionMatchSettingsMobile from "../components/popup/mobile/description-match-settings-mobile";
import ProvisionTransactionsDisplayMobile from "../components/popup/mobile/provision-transactions-display-mobile";
import PatternAnalysisDisplayMobile from "../components/popup/mobile/pattern-analysis-display-mobile";
import SimulationDetailMobile from "../components/popup/mobile/simulation-detail-mobile";
import AddTransactionPopupMobile from "../components/popup/mobile/add-transaction-mobile";
import AddTransactionPopup from "../components/popup/add-transaction";
import PatternAnalysisDisplay from "../components/pattern-analysis-display";
import ProvisionTransactionsDisplay from "../components/provision-transactions-display";
import axios from 'axios';
import Cookies from 'js-cookie';
import { endServerSession } from '../util/session';

import useApi from "../hooks/use-api";
import routes from "../api/routes";
import buildApiRequestConfig from "../api/url-builder";
import { useUser } from "../providers/user-provider";
import { useToast } from "../providers/toaster-provider";
import { useSettings } from "../providers/settings-provider";
import { usePermissions } from "../providers/permissions-provider";
import { startOfYear, endOfYear, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, set } from "date-fns";
import { computeRunningBalance, applyDisplayFilters, sortTransactions, groupTransactionsClient, computeHighLow, computeCashFlowStatusClient } from "../util/transaction-calculations";

const baseColumns = [
    { id: 'date', label: 'Date', width: 100 },
    // Fits up to "$ -99,999.99" at its base width - the width every column
    // falls back to on a narrow screen (wider screens grow it). 100 was too
    // tight once the cell's own padding was counted: the right-aligned value
    // overflowed to the left and the "$" got clipped (e.g. $ 25,255.00).
    { id: 'amount', label: 'Amount', width: 120 },
    // Just enough for up to $9,999,999.99 (plus a negative sign).
    { id: 'balance', label: 'Cash Position', width: 130 },
    { id: 'description', label: 'Description', width: 500 },
    // Shows initials only (see the 'bank' case in transactions-table-virtual.tsx) -
    // the full bank/account name is still available via tooltip.
    { id: 'bank', label: 'Bank', width: 90 },
    { id: 'category', label: 'Category', width: 200 },
];

// The former single "Action" column, split into one compact column per
// icon: Linked / Moved status icons, the row checkbox, and the row menu.
const ICON_COLUMNS = [
    { id: 'linked', label: 'Linked', width: 26 },
    { id: 'moved', label: 'Moved', width: 26 },
    { id: 'select', label: 'Select', width: 26 },
    { id: 'actions', label: 'Actions', width: 30 },
];
const ICON_COLUMN_IDS = new Set(ICON_COLUMNS.map(c => c.id));

export type ImportRow = {
    entityId?: number;
    date?: string;
    amount?: number;
    description?: string;
    categoryId?: number;
    bankId?: number;
    linkedTransactionId?: string;
    // Original CSV row (every column, unmapped) - stored per-transaction on
    // the backend as sourceData.raw.
    sourceData?: unknown;
};

export type FileDetails = {
    file?: File;
    parsedRows?: Record<string, string>[];
    failedRows?: Record<string, string>[];
    result: {
        successfulRows: number | 0;
        failedRows: number | 0;
        totalRows: number | 0;
    }
    headers?: string[];
};

// Server-side filters only - the ones that actually reduce the row count
// returned (see transaction-controller.js). Date range, sort, grouping, and
// balance are all derived client-side now (see util/transaction-calculations.ts)
// from this one full fetch, with no pagination.
type TransactionBody = {
    showReal?: boolean;
    showRecurring?: boolean;
    showOneTime?: boolean;
    bankIds?: string;
    categoryIds?: string;
    simulationId?: string;
    // Still sent through for simulation, which stays server-computed
    // (including its own display filters/sort/group - see
    // transaction-controller.js).
    startDate?: string;
    endDate?: string;
    viewBy?: string;
    filterConfig?: string;
    // Omit showReal/showRecurring/showOneTime/bankIds/categoryIds and set
    // this instead to have the server resolve them from the user's saved
    // filter (see transaction-controller.js) - used only for the initial
    // fetch, before GET /filters or this same resolution has told the
    // client what those actually are.
    useSavedFilters?: boolean;
};

const TransactionsVirtual: React.FC = () => {
    const [provisionTheseSourceTransactions, setProvisionTheseSourceTransactions] = React.useState<any[]>([]);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [openAddEntity, setOpenAddEntity] = React.useState(searchParams.get('createCompany') === 'true');
    const { user, setUser } = useUser();
    const { can } = usePermissions();
    const canCreateTransactions = can('transactions', 'create');
    const canUpdateTransactions = can('transactions', 'update');
    const canDeleteTransactions = can('transactions', 'delete');
    // Provision rows on this page follow the Provisions permission; creating a
    // provision from transactions also counts the "Add Provision" sub-action.
    const canUpdateProvisions = can('provisions', 'update');
    const canDeleteProvisions = can('provisions', 'delete');
    const canCreateProvisions = can('provisions', 'create') || can('transactions.addProvision', 'create');
    const { showError, showSuccess } = useToast();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    // Picks the bottom-sheet mobile variant vs the desktop centered dialog -
    // same prop contract on both, so every call site below can just render
    // "the popup" without its own isMobile branch.
    const DeleteModal = isMobile ? DeletePopupMobile : DeletePopup;
    const DateConfirmModal = isMobile ? DateConfirmPopupMobile : DateConfirmPopup;
    const ChangeProvisionDateModal = isMobile ? ChangeProvisionDatePopupMobile : ChangeProvisionDatePopup;
    const DescriptionMatchSettingsModal = isMobile ? DescriptionMatchSettingsMobile : DescriptionMatchSettings;
    const ProvisionTransactionsDisplayModal = isMobile ? ProvisionTransactionsDisplayMobile : ProvisionTransactionsDisplay;
    const PatternAnalysisDisplayModal = isMobile ? PatternAnalysisDisplayMobile : PatternAnalysisDisplay;
    const SimulationDetailModal = isMobile ? SimulationDetailMobile : SimulationDetail;
    const AddTransactionModal = isMobile ? AddTransactionPopupMobile : AddTransactionPopup;
    const { sidebarOpen, setSidebarOpen } = useSidebarMobile();
    // Watch for URL changes to open entity modal
    React.useEffect(() => {
        setOpenAddEntity(searchParams.get('createCompany') === 'true');
    }, [searchParams]);

    const { companySettings } = useSettings();
    const [doneImporting, setDoneImporting] = React.useState(false);
    const [unitValue, setUnitValue] = React.useState('month');
    const [isReanalyze, setIsReanalyze] = React.useState(false);
    
    // Helper function to calculate start date from visibleHistory
    const calculateStartDateFromVisibleHistory = (settings?: any): Date => {
        let start = new Date();
        const visibleHistoryValue = settings?.settings?.visibleHistory || companySettings?.settings?.visibleHistory;
        
        if (visibleHistoryValue) {
            const match = visibleHistoryValue.match(/^(\d+)\s*(day|week|month|quarter|year|d|w|m|q|y)s?$/i);
            if (match) {
                const value = parseInt(match[1], 10);
                let unit = match[2].toLowerCase();

                // Normalize short forms to full unit names
                if (unit.length === 1) {
                    switch (unit) {
                        case 'd':
                            unit = 'day';
                            break;
                        case 'w':
                            unit = 'week';
                            break;
                        case 'm':
                            unit = 'month';
                            break;
                        case 'q':
                            unit = 'quarter';
                            break;
                        case 'y':
                            unit = 'year';
                            break;
                        default:
                            break;
                    }
                }

                start = new Date();
                switch (unit) {
                    case 'day':
                        start.setDate(start.getDate() - value);
                        break;
                    case 'week':
                        start.setDate(start.getDate() - value * 7);
                        break;
                    case 'month':
                        start.setMonth(start.getMonth() - value);
                        break;
                    case 'quarter':
                        start.setMonth(start.getMonth() - value * 3);
                        break;
                    case 'year':
                        start.setFullYear(start.getFullYear() - value);
                        break;
                    default:
                        break;
                }
            } else {
                // Default to 7 days if visibleHistory is not in expected format
                start = new Date();
                start.setDate(start.getDate() - 7);
            }
        } else {
            // Default to 7 days if visibleHistory is not set
            start = new Date();
            start.setDate(start.getDate() - 7);
        }
        return start;
    };

    // Helper function to calculate end date based on unitValue
    const calculateEndDateFromUnitValue = (valueOverride?: string): Date => {
        const valueStr = valueOverride || unitValue;
        if (!valueStr) return new Date();

        const keyword = valueStr.toLowerCase();
        const now = new Date();
        switch (keyword) {
            case 'day':
                now.setDate(now.getDate() + 1);
                return now;
            case 'week':
                now.setDate(now.getDate() + 7);
                return now;
            case 'month':
                now.setMonth(now.getMonth() + 1);
                return now;
            case 'quarter':
                now.setMonth(now.getMonth() + 3);
                return now;
            case 'year':
                now.setFullYear(now.getFullYear() + 1);
                return now;
            default:
                break;
        }

        // First try compact format like "30D", "4W", "1M", "1Q", "1Y"
        const compactMatch = valueStr.match(/^(\d+)([DWMQY])$/i);
        if (compactMatch) {
            const value = parseInt(compactMatch[1], 10);
            const unit = compactMatch[2].toUpperCase();
            const now2 = new Date();

            switch (unit) {
                case 'D':
                    now2.setDate(now2.getDate() + value);
                    break;
                case 'W':
                    now2.setDate(now2.getDate() + value * 7);
                    break;
                case 'M':
                    now2.setMonth(now2.getMonth() + value);
                    break;
                case 'Q':
                    now2.setMonth(now2.getMonth() + value * 3);
                    break;
                case 'Y':
                    now2.setFullYear(now2.getFullYear() + value);
                    break;
                default:
                    break;
            }
            return now2;
        }
        
        // Fallback to verbose format like "30 days", "1 month", etc.
        const match = valueStr.match(/^(\d+)\s*(day|week|month|quarter|year)s?$/i);
        if (!match) return new Date();
        const value = parseInt(match[1], 10);
        const unit = match[2].toLowerCase();
        const now3 = new Date();

        switch (unit) {
            case 'day':
                now3.setDate(now3.getDate() + value);
                break;
            case 'week':
                now3.setDate(now3.getDate() + value * 7);
                break;
            case 'month':
                now3.setMonth(now3.getMonth() + value);
                break;
            case 'quarter':
                now3.setMonth(now3.getMonth() + value * 3);
                break;
            case 'year':
                now3.setFullYear(now3.getFullYear() + value);
                break;
            default:
                break;
        }
        return now3;
    };
    
    const [startDate, setStartDate] = React.useState<Date>(() => {
        return calculateStartDateFromVisibleHistory();
    });
    const [endDate, setEndDate] = React.useState<Date>(() => {
        return calculateEndDateFromUnitValue();
    });


    const [bank, setBank] = React.useState<string[]>([]);
    const [category, setCategory] = React.useState<string[]>([]);
    const [viewBy, setViewBy] = React.useState('transactions');
    const [taxOptions, setTaxOptions] = React.useState<{ id: number; name: string; rate: number }[]>([]);
    const defaultColumns = React.useMemo(() => [
        ...baseColumns,
        ...taxOptions.map(t => ({ id: `tax_${t.id}`, label: t.name, width: 50 })),
        // One narrow column per icon, each headed by its own icon (the label
        // shows as its tooltip) - see ICON_COLUMN_IDS in
        // transactions-table-virtual.tsx.
        ...ICON_COLUMNS,
    ], [taxOptions]);

    const [columns, setColumns] = React.useState(defaultColumns);
    const [filterConfig, setFilterConfig] = React.useState<FilterParams | undefined>();
    // Mobile-only: dense table row layout (date/description/amount/cash
    // position only, no checkbox or action menu) as an alternative to the
    // detailed card - lets more rows fit on screen at once.
    const [compactView, setCompactView] = React.useState(false);
    const [fileDetails, setFileDetails] = React.useState<FileDetails | undefined>();
    const [importBankId, setImportBankId] = React.useState<number | null>(null);
    const [simulationId, setSimulationId] = React.useState<{ id: string; name: string; simulationDetails?: any[] } | null>(null);
    const [favoriteSimulations, setFavoriteSimulations] = React.useState<Array<{ id: string; name: string }>>([]);
    const [companyStatus, setCompanyStatus] = React.useState<{ 
        currentBalance?: number, 
        firstNegativeDate?: Date, 
        cashflowMessage?: string,
        daysBeforeNegative?: string,
        firstNegativeTransaction?: {
            balance?: number,
            date?: Date,
            guid?: string,
        },
        wentNegative?: boolean,
        highestAmount?: number,
        lowestAmount?: number,
        latestTransaction?: Date | null,
        earliestTransaction?: Date | null,
        // New API fields
        startOfRecords?: { guid?: string | null; date?: Date | string | null } | null;
        endOfHorizon?: Date | string | null;
        totalCount?: number,
        includePushedForwardInBalance?: boolean,
    }>({});

    // Raw, unbalanced transaction rows from the server (already filtered by
    // real/recurring/one-time/bank/category, or - for simulation - already
    // fully balanced/filtered/sorted/grouped server-side). Balance, display
    // filters, sort, and grouping are derived from this client-side below
    // (see util/transaction-calculations.ts) instead of being recomputed by
    // the server on every date-range/sort/group change.
    const [rawTransactions, setRawTransactions] = React.useState<any[]>([]);
    const [totalOpeningBalance, setTotalOpeningBalance] = React.useState<number>(0);

    const [hierarchicalCategories, setHierarchicalCategories] = React.useState<any[]>([]);
    const [banks, setBanks] = React.useState<DropdownOption[]>([]);
    const [rawBanks, setRawBanks] = React.useState<any[]>([]);
    const [isDateRange, setIsDateRange] = React.useState(false);

    // Keep start/end in sync when unitValue changes (UnitSelect moved into date-column popover)
    React.useEffect(() => {
        if (isDateRange) return; // manual range already set by date picker

        // if unitValue is one of our semantic keywords, calculate span relative to today
        const keyword = unitValue?.toLowerCase();
        const now = new Date();
        if (keyword && ['week','month','quarter','year'].includes(keyword)) {
            let s = new Date(now);
            switch (keyword) {
                case 'week':
                    s.setDate(s.getDate() - 7);
                    break;
                case 'month':
                    s.setMonth(s.getMonth() - 1);
                    break;
                case 'quarter':
                    s.setMonth(s.getMonth() - 3);
                    break;
                case 'year':
                    s.setFullYear(s.getFullYear() - 1);
                    break;
            }
            setStartDate(s);
            setEndDate(calculateEndDateFromUnitValue(keyword));
        } else {
            // fall back to original behaviour for numeric/legacy values or custom
            setStartDate(calculateStartDateFromVisibleHistory());
            setEndDate(calculateEndDateFromUnitValue());
        }
    }, [unitValue, isDateRange, companySettings]);
    
    const hierarchicalCategoryOptions = React.useMemo(() => {
        const transformForTree = (cat: any): any => ({
            id: cat.id.toString(),
            label: cat.name,
            children: cat.children ? cat.children.map(transformForTree) : undefined
        });
        
        const categoryOptions = (Array.isArray(hierarchicalCategories) ? hierarchicalCategories : []).map(transformForTree);
        
        return [
            { 
                id: 'select-all-categories', 
                label: 'Select All', 
                isSpecial: true, 
                specialType: 'selectAll' as const 
            },
            ...categoryOptions
        ];
    }, [hierarchicalCategories]);

    const categoryDefaultTaxIdsMap = React.useMemo(() => {
        const map: Record<string, number[]> = {};
        const walk = (cats: any[]) => {
            cats.forEach((cat: any) => {
                map[String(cat.id)] = cat.defaultTaxIds || [];
                if (cat.children?.length) walk(cat.children);
            });
        };
        walk(Array.isArray(hierarchicalCategories) ? hierarchicalCategories : []);
        return map;
    }, [hierarchicalCategories]);

    const bankTreeOptions = React.useMemo(() => {
        return [
            { 
                id: 'select-all-banks', 
                label: 'Select All', 
                isSpecial: true, 
                specialType: 'selectAll' as const 
            },
            { 
                id: 'no-bank-account', 
                label: 'No Bank Assigned', 
                specialType: 'noBankAccount' as const 
            },
            ...banks.map(bank => ({
                id: String(bank.value),
                label: bank.label
            }))
        ];
    }, [banks]);

    const flatCategoryDropdownOptions = React.useMemo(() => {
        const flatten = (cats: any[]): DropdownOption[] => {
            const result: DropdownOption[] = [];
            cats.forEach((cat: any) => {
                result.push({ value: cat.id.toString(), label: cat.name });
                if (cat.children && cat.children.length > 0) {
                    result.push(...flatten(cat.children));
                }
            });
            return result;
        };
        return flatten(Array.isArray(hierarchicalCategories) ? hierarchicalCategories : []);
    }, [hierarchicalCategories]);
    
    const [selectedTransactionId, setSelectedTransactionId] = React.useState<string | null>(null);
    const [selectedInvoiceNumber, setSelectedInvoiceNumber] = React.useState<string | null>(null);
    const [autoOpenLinkModal, setAutoOpenLinkModal] = React.useState(false);
    const [editingProvisionId, setEditingProvisionId] = React.useState<number | null>(null);
    const [deleteOption, setDeleteOption] = React.useState<string>('this-occurrence');
    const [isRecurringProvisioning, setIsRecurringProvisioning] = React.useState(false);
    const [provisionThisData, setProvisionThisData] = React.useState<{ date?: string; amount?: number | string; description?: string; sourceTransactionGuid?: string } | null>(null);
    const [analysisData, setAnalysisData] = React.useState<any>(null);

    // when provisioning a transaction that isn't recurring we want the default
    // start date in the modal to begin one period out (monthly is the default)
    const adjustStartDate = (dateStr: string | undefined, analysis?: any): string | undefined => {
        if (!dateStr) return dateStr;
        const interval = analysis?.interval?.toString()?.toLowerCase();
        if (interval === 'unknown' || interval === 'not recurring' || interval === 'none') {
            const d = new Date(dateStr);
            d.setMonth(d.getMonth() + 1);
            return d.toISOString().split('T')[0];
        }
        return dateStr;
    };
    const [sourceTransaction, setSourceTransaction] = React.useState<any>(null);
    const [sourceTransactionGuid, setSourceTransactionGuid] = React.useState<string | undefined>(undefined);
    const [patternAnalysisMessage, setPatternAnalysisMessage] = React.useState<string>('');
    const [showPatternConfirmation, setShowPatternConfirmation] = React.useState(false);
    // Debug-off path: matches were found but the full review window is skipped,
    // so the cutoff warning (if any) needs its own standalone confirm step
    // before auto-continuing straight to the provision form.
    const [showCutoffConfirmDebugOff, setShowCutoffConfirmDebugOff] = React.useState(false);
    const pendingCutoffConfirmRef = React.useRef<{ guid?: string; analysis: any; source: any } | null>(null);
    const transactionsTableRef = React.useRef<TransactionsTableHandle>(null);
    const transactionsTableMobileRef = React.useRef<TransactionsTableMobileHandle>(null);
    // A single user action (e.g. "Link and Close Transaction") can resolve
    // as multiple independent async calls under the hood - link, unlink, and
    // the open/closed toggle each fire and resolve on their own, and each one
    // calls the desktop TransactionDetail's onLink separately. Capturing the
    // scroll anchor and bumping refreshTrigger on every one of those races
    // the anchor against a table already wiped by an earlier one in the same
    // burst - coalesce them into a single capture (taken on the first call,
    // while the table is still intact) and a single refetch.
    const pendingLinkRefreshRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    // description-only matching settings dialog state
    const [showMatchSettings, setShowMatchSettings] = React.useState(false);
    // description-only match settings handled by separate component
    const [pendingDescriptionGuid, setPendingDescriptionGuid] = React.useState<string | null>(null);
    const [pendingDescriptionText, setPendingDescriptionText] = React.useState<string>('');
    const [descriptionOptions, setDescriptionOptions] = React.useState<{start: number; length: number} | null>(null);
    const [showDebug, setShowDebug] = React.useState<boolean>(() => {
        const savedDebugState = localStorage.getItem('showDebug');
        return savedDebugState !== null ? JSON.parse(savedDebugState) : false;
    });
    // Perf-diagnostic toggle - now that balance/cash-flow-status calculation
    // happens client-side (see util/transaction-calculations.ts), this lets
    // that calculation be switched off entirely to check whether it's the
    // client-side bottleneck, without needing a code change to test. Only
    // visible in the topbar when the "Show Cash Position Toggle" secret
    // option is on (/secret-options); does not affect simulation rows, which
    // always arrive already balanced from the server regardless.
    const [showCashPositionToggleFeature, setShowCashPositionToggleFeature] = React.useState<boolean>(() => {
        const saved = localStorage.getItem('showCashPositionToggle');
        return saved !== null ? JSON.parse(saved) : false;
    });
    const [cashPositionEnabled, setCashPositionEnabled] = React.useState<boolean>(() => {
        const saved = localStorage.getItem('cashPositionEnabled');
        return saved !== null ? JSON.parse(saved) : true;
    });
    React.useEffect(() => {
        localStorage.setItem('cashPositionEnabled', JSON.stringify(cashPositionEnabled));
    }, [cashPositionEnabled]);
    const [showAnalysisDialog, setShowAnalysisDialog] = React.useState(false);
    const [isReanalyzeInProgress, setIsReanalyzeInProgress] = React.useState(false);
    const [isDescriptionOnlyMatch, setIsDescriptionOnlyMatch] = React.useState(false);
    const reanalyzeCancelledRef = React.useRef(false);
    const lastShownProvisionSuccessIdRef = React.useRef<number | null>(null);
    const processedMarkTrxRef = React.useRef<any>(null);
    const [data, setData] = React.useState([] as TransactionRow[]);
    const [refreshTrigger, setRefreshTrigger] = React.useState(0);

    // Handle opening provision detail from invoices page via URL params.
    // Must go through React Router's own setSearchParams (not a raw
    // window.history.replaceState call) - bypassing it desyncs React
    // Router's internal location tracking from the real browser history
    // stack, since replaceState doesn't fire the events React Router
    // listens for. Once desynced, a later navigate() call from elsewhere
    // (e.g. the invoice-number link in the transaction detail popup) can
    // update the visible URL while React Router's own render never catches
    // up, and back/forward then throws "Maximum update depth exceeded"
    // when it resyncs. Depends on the extracted string, not the
    // searchParams object itself, since useSearchParams() returns a new
    // object every render - depending on the object would refire this
    // effect every render and loop forever.
    const invoiceNumberParam = searchParams.get('invoiceNumber');
    React.useEffect(() => {
        if (!invoiceNumberParam) return;
        // Open the detail modal - it will fetch the provision by invoiceNumber
        setSelectedInvoiceNumber(invoiceNumberParam);
        setOpenModal('detail');
        // Remove the param from URL to avoid re-triggering
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('invoiceNumber');
        setSearchParams(newParams, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invoiceNumberParam]);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [saveFilters, setSaveFilters] = useApi();

    /* SUPERSEDED - bidirectional cursor pagination, no longer used now that
       the server returns the entire filtered set in one request
       (ENABLE_LAZY_LOADING=false in web.config) and the client derives
       display/sort/group locally. Kept commented out for reference/rollback.

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [isInitialLoad, setIsInitialLoad] = React.useState(true);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [totalPages, setTotalPages] = React.useState(0);

    // Pagination state
    const [currentPage, setCurrentPage] = React.useState(1);
    const [pageSize] = React.useState(50);
    // hasNextPage/isLoadingMore/onLoadMore = "older" direction (unchanged
    // names/behavior - scrolling down, further into the past).
    const [hasNextPage, setHasNextPage] = React.useState(false);
    const [isLoadingMore, setIsLoadingMore] = React.useState(false);
    // "newer" direction (scrolling up, back toward/past the anchor date).
    const [hasNewer, setHasNewer] = React.useState(false);
    const [isLoadingNewer, setIsLoadingNewer] = React.useState(false);
    // Bounds (within the server's full sorted result) of what's currently
    // loaded into `data` - needed because once the first page is anchored on
    // today instead of always starting at offset 0, "page number" no longer
    // identifies a request; the actual offset does.
    const [loadedOffset, setLoadedOffset] = React.useState(0);
    const [loadedEndIndex, setLoadedEndIndex] = React.useState(0);
    // What kind of request is in flight, read by the fetch-result effect to
    // decide whether to replace, append, or prepend `data`.
    const pendingLoadDirectionRef = React.useRef<'replace' | 'older' | 'newer'>('replace');
    */

    const [isReal, setIsReal] = React.useState(true);
    const [isRecurringProvisions, setIsRecurringProvisions] = React.useState(true);
    const [isOneTimeProvisions, setIsOneTimeProvisions] = React.useState(true);
    const [openModal, setOpenModal] = React.useState('');

    const [showBasiqImport, setShowBasiqImport] = React.useState(false);
    const [showProvisionTransactions, setShowProvisionTransactions] = React.useState(false);
    const [createdProvisionId, setCreatedProvisionId] = React.useState<number | null>(null);
    // Mirrors createdProvisionId synchronously - handleConfirmMarkTransactions reads
    // this instead of the state value so the target id can never lag a render behind
    // whatever template was actually just created.
    const createdProvisionIdRef = React.useRef<number | null>(null);
    const setCreatedProvisionIdBoth = React.useCallback((id: number | null) => {
        createdProvisionIdRef.current = id;
        setCreatedProvisionId(id);
    }, []);
    const [provisionTransactions, setProvisionTransactions] = React.useState<any[]>([]);

    // Listen for changes to showDebug / showCashPositionToggle in localStorage
    React.useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === 'showDebug' && e.newValue !== null) {
                setShowDebug(JSON.parse(e.newValue));
            }
            if (e.key === 'showCashPositionToggle' && e.newValue !== null) {
                setShowCashPositionToggleFeature(JSON.parse(e.newValue));
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    const [progressModal, setProgressModal] = React.useState({
        open: false,
        currentChunk: 0,
        totalChunks: 0,
        processedTransactions: 0,
        totalTransactions: 0,
        status: 'processing' as 'processing' | 'success' | 'error'
    });

    const [fetchCategories, setFetchCategories] = useApi();
    const [fetchBanks, setFetchBanks] = useApi();

    const [fetch, setFetch] = useApi();
    // const [fetchCompanyStatus, setFetchCompanyStatus] = useApi(); // SUPERSEDED - see computeCashFlowStatusClient in util/transaction-calculations.ts
    const [deleteTransaction, setDeleteTransaction] = useApi();
    
    // Provision API hooks
    const [createProvision, setCreateProvision] = useApi();
    const [updateProvision, setUpdateProvision] = useApi();
    const [analyzePattern, setAnalyzePattern] = useApi();
    const [analyzePatternByAI, setAnalyzePatternByAI] = useApi();
    const [analyzeMultiple, setAnalyzeMultiple] = useApi();
    
    // Transaction API hooks
    const [createTransaction, setCreateTransaction] = useApi();
    const [updateTransaction, setUpdateTransaction] = useApi();
    // A manually-added transaction (the "+ Transaction" kind) currently being
    // edited in the add-transaction form - null when that form is adding a new one.
    const [manualEditTarget, setManualEditTarget] = React.useState<TransactionRow | null>(null);
    // The pending delete targets a manually-added real transaction, which has its
    // own route (the generic delete route is provision-only).
    const [deleteIsManual, setDeleteIsManual] = React.useState(false);
    // Stable identity matters: the add form re-prefills whenever this changes.
    const manualEditInitial = React.useMemo(() => {
        if (!manualEditTarget) return null;
        const bankId = Number(manualEditTarget.bank);
        return {
            amount: manualEditTarget.amount,
            description: manualEditTarget.description,
            date: manualEditTarget.date,
            categoryId: Number(manualEditTarget.category) || null,
            bankAccountId: Number.isFinite(bankId) && bankId > 0 ? bankId : null,
        };
    }, [manualEditTarget]);

    // Filter API hooks
    const [fetchFilters, setFetchFilters] = useApi();
    const [fetchSimulations, setFetchSimulations] = useApi();
    const [fetchSummary, setFetchSummary] = useApi();

    // Transaction update API hooks
    const [updateTransactionCategory, setUpdateTransactionCategory] = useApi();
    const [updateTransactionTax, setUpdateTransactionTax] = useApi();
    const [updateProvisionDate, setUpdateProvisionDate] = useApi();
    const [changeDateTarget, setChangeDateTarget] = React.useState<TransactionRow | null>(null);
    const [terminate, setTerminate] = useApi();
    const [terminateTarget, setTerminateTarget] = React.useState<TransactionRow | null>(null);
    const [terminateEndDate, setTerminateEndDate] = React.useState<string>('');

    const [fetchTaxDefs, setFetchTaxDefs] = useApi();

    const [markTrx, setMarkTrx] = useApi();
    const [createCompany, setCreateCompany] = useApi();

    const [initialLoadComplete, setInitialLoadComplete] = React.useState(false);
    const [categoriesLoaded, setCategoriesLoaded] = React.useState(false);
    const [banksLoaded, setBanksLoaded] = React.useState(false);
    const [filtersLoaded, setFiltersLoaded] = React.useState(false);
    // True once real/recurring/one-time/bank/category have come from either
    // GET /filters or the transaction fetch's resolvedFilters (useSavedFilters
    // below) - whichever arrives first. Prevents the other source from
    // re-applying the same values later.
    const filterDefaultsResolvedRef = React.useRef(false);
    // Set alongside filterDefaultsResolvedRef, whichever source wins the
    // race - suppresses the one redundant fetch that isReal/bank/category
    // changing would otherwise trigger, since the data those values would
    // fetch was already fetched (server-resolved) by the bootstrap request.
    const skipNextFetchRef = React.useRef(false);
    const [simulationsList, setSimulationsList] = React.useState<Array<any>>([]);
    const [summaryData, setSummaryData] = React.useState<any>(null);
    const [stage, setStage] = React.useState<'initialize' | 'fetching' | 'complete'>('initialize');

    const isDrillDownRef = React.useRef(false);
    const isInitialLoadRef = React.useRef(true);
    const lastCreateCompanyDataRef = React.useRef<any>(null);

    // Wrapper functions to clear applied simulation when filters are manually toggled
    const handleIsRealChange = (value: boolean) => {
        if (simulationId) {
            setSimulationId(null);
        }
        setIsReal(value);
    };

    const handleIsRecurringProvisionsChange = (value: boolean) => {
        if (simulationId) {
            setSimulationId(null);
        }
        setIsRecurringProvisions(value);
    };

    const handleIsOneTimeProvisionsChange = (value: boolean) => {
        if (simulationId) {
            setSimulationId(null);
        }
        setIsOneTimeProvisions(value);
    };

    // helper to map a pair of dates to one of the canned range names
    const getDateRangeOption = (s: Date, e: Date, isRange: boolean): string => {
        if (isRange) return 'custom';
        const now = new Date();
        const sameDay = (d1: Date, d2: Date) => d1.toDateString() === d2.toDateString();
        // week
        let start = new Date(now);
        start.setDate(start.getDate() - 7);
        let end = new Date(now);
        end.setDate(end.getDate() + 7);
        if (sameDay(start, s) && sameDay(end, e)) return 'week';
        // month
        start = new Date(now); start.setMonth(start.getMonth() - 1);
        end = new Date(now); end.setMonth(end.getMonth() + 1);
        if (sameDay(start, s) && sameDay(end, e)) return 'month';
        // quarter
        start = new Date(now); start.setMonth(start.getMonth() - 3);
        end = new Date(now); end.setMonth(end.getMonth() + 3);
        if (sameDay(start, s) && sameDay(end, e)) return 'quarter';
        // year
        start = new Date(now); start.setFullYear(start.getFullYear() - 1);
        end = new Date(now); end.setFullYear(end.getFullYear() + 1);
        if (sameDay(start, s) && sameDay(end, e)) return 'year';
        return 'month'; // fallback default
    };

    // Debounced save function for regular filter changes
    const saveFiltersDebounced = React.useCallback(() => {
        if (!user || !filtersLoaded || isDrillDownRef.current) return;

        const processedBankIds = bank.filter(id => !['select-all-banks'].includes(id)).map(id => {
            if (id === 'no-bank-account') {
                return 'null'; 
            }
            return id;
        });

        const processedCategoryIds = category.filter(id => !['select-all-categories'].includes(id));
        
        const filterData = {
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0],
            showReal: isReal,
            showRecurring: isRecurringProvisions,
            showOneTime: isOneTimeProvisions,
            viewBy: viewBy,
            bankIds: processedBankIds.length > 0 ? processedBankIds.join(',') : null,
            categoryIds: processedCategoryIds.length > 0 ? processedCategoryIds.join(',') : null,
            unitValue: isDateRange ? getDateRangeOption(startDate, endDate, isDateRange) : unitValue,
            entityId: user.entityId,
            appliedSimId: simulationId?.id || null,
            appliedSimName: simulationId?.name || null
        };

        const { url, method } = buildApiRequestConfig(routes.filter.save, [{ param: 'userGuid', value: user.guid }]);
        setSaveFilters({ url, method, body: filterData });
    }, [user, startDate, endDate, isReal, isRecurringProvisions, isOneTimeProvisions, viewBy, bank, category, unitValue, isDateRange, simulationId]);

    // Immediate save function for drill-down operations
    const saveFiltersImmediately = React.useCallback((newViewBy?: string) => {
        if (!user || !filtersLoaded) return;

        const processedBankIds = bank.filter(id => !['select-all-banks'].includes(id)).map(id => {
            if (id === 'no-bank-account') {
                return 'null'; 
            }
            return id;
        });

        const processedCategoryIds = category.filter(id => !['select-all-categories'].includes(id));
        
        const filterData = {
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0],
            showReal: isReal,
            showRecurring: isRecurringProvisions,
            showOneTime: isOneTimeProvisions,
            viewBy: newViewBy || viewBy,
            bankIds: processedBankIds.length > 0 ? processedBankIds.join(',') : null,
            categoryIds: processedCategoryIds.length > 0 ? processedCategoryIds.join(',') : null,
            unitValue: isDateRange ? getDateRangeOption(startDate, endDate, isDateRange) : unitValue,
            appliedSimId: simulationId?.id || null,
            appliedSimName: simulationId?.name || null
        };

        const { url, method } = buildApiRequestConfig(routes.filter.save, [{ param: 'userGuid', value: user.guid }]);
        setSaveFilters({ url, method, body: filterData });
    }, [user, filtersLoaded, startDate, endDate, isReal, isRecurringProvisions, isOneTimeProvisions, viewBy, bank, category, unitValue, isDateRange, simulationId]);

    // Load filters on component mount
    useEffect(() => {
        if (!user) return;

        const { url, method } = buildApiRequestConfig(routes.filter.fetchByGuid, [{ param: 'userGuid', value: user.guid }, { param: 'entityId', value: user.entityId }]);
        setFetchFilters({ url, method });
    }, [user]);

    // Handle loaded filters
    useEffect(() => {
        const { data, isLoading } = fetchFilters;
        if (data && !isLoading) {
            // interpret saved unitValue; keyword ranges or legacy values
            const uv = data.unitValue;
            if (uv && ['week','month','quarter','year','custom'].includes(uv)) {
                if (uv === 'custom') {
                    // use the saved explicit dates and treat as manual range
                    setIsDateRange(true);
                    setUnitValue('custom');
                    if (data.startDate) {
                        setStartDate(new Date(data.startDate));
                    }
                    if (data.endDate) {
                        setEndDate(new Date(data.endDate));
                    }
                } else {
                    // recalc start/end based on current date and keyword
                    const now = new Date();
                    let s = new Date(now);
                    let e = new Date(now);
                    switch (uv) {
                        case 'week':
                            s.setDate(s.getDate() - 7);
                            e.setDate(e.getDate() + 7);
                            break;
                        case 'month':
                            s.setMonth(s.getMonth() - 1);
                            e.setMonth(e.getMonth() + 1);
                            break;
                        case 'quarter':
                            s.setMonth(s.getMonth() - 3);
                            e.setMonth(e.getMonth() + 3);
                            break;
                        case 'year':
                            s.setFullYear(s.getFullYear() - 1);
                            e.setFullYear(e.getFullYear() + 1);
                            break;
                        default:
                            break;
                    }
                    setStartDate(s);
                    setEndDate(e);
                    setIsDateRange(false);
                    setUnitValue(uv);
                }
            } else {
                // fallback to original behaviour using numeric/legacy string
                if (uv && uv !== '') {
                    setUnitValue(uv);
                    setIsDateRange(false);
                    const newStartDate = calculateStartDateFromVisibleHistory();
                    const newEndDate = calculateEndDateFromUnitValue(uv);
                    setStartDate(newStartDate);
                    setEndDate(newEndDate);
                } else {
                    setIsDateRange(true);
                    setUnitValue('');
                    if (data.startDate) {
                        setStartDate(new Date(data.startDate));
                    }
                    if (data.endDate) {
                        setEndDate(new Date(data.endDate));
                    }
                }
            }

            setViewBy(data.viewBy);

            // Only apply if the transaction fetch's resolvedFilters hasn't
            // already set these (see useSavedFilters/filterDefaultsResolvedRef).
            if (!filterDefaultsResolvedRef.current) {
                filterDefaultsResolvedRef.current = true;
                skipNextFetchRef.current = true;
                setIsReal(data.showReal === null || data.showReal === undefined
                    ? true
                    : data.showReal === true);
                setIsRecurringProvisions(data.showRecurring === null || data.showRecurring === undefined
                    ? true
                    : data.showRecurring === true);
                setIsOneTimeProvisions(data.showOneTime === null || data.showOneTime === undefined
                    ? true
                    : data.showOneTime === true);

                const bankIds = data.bankIds?.split(',') || [];
                const processedBankIds = bankIds.map((id: string) => {
                    if (id === 'null' || id === '') {
                        return 'no-bank-account';
                    }
                    return id;
                });
                setBank(processedBankIds);

                setCategory(data.categoryIds?.split(',') || []);
            }

            // Parse and store favorite simulation IDs
            if (data.favoriteSimIds) {
                const favIds = typeof data.favoriteSimIds === 'string' 
                    ? data.favoriteSimIds.split(',').map((id: string) => id.trim()).filter((id: string) => id)
                    : Array.isArray(data.favoriteSimIds)
                    ? data.favoriteSimIds
                    : [];
                
                // Map IDs to simulation objects if simulationsList is available
                if (simulationsList && simulationsList.length > 0) {
                    const favSimulations = favIds
                        .map((id: string) => {
                            const sim = simulationsList.find(s => String(s.id) === String(id));
                            return sim ? { id: String(sim.id), name: sim.name } : null;
                        })
                        .filter((sim: any) => sim !== null);
                    setFavoriteSimulations(favSimulations);
                } else {
                    // If simulations not loaded yet, just store IDs as fallback
                    setFavoriteSimulations(favIds.map((id: string) => ({ id: String(id), name: String(id) })));
                }
            }
            
            // Restore applied simulation if saved
            if (data.appliedSimId) {
                // Try to find the simulation name from the loaded simulations list
                const foundSimulation = simulationsList.find(sim => String(sim.id) === String(data.appliedSimId));
                if (foundSimulation?.name) {
                    setSimulationId({
                        id: String(data.appliedSimId),
                        name: foundSimulation.name
                    });
                } else {
                    // Simulations not loaded yet, just store the ID for now
                    setSimulationId({
                        id: String(data.appliedSimId),
                        name: null
                    } as any);
                }
            } else {
                setSimulationId(null);
            }
            
            // Mark filters as loaded AFTER all state updates to prevent race conditions
            setFiltersLoaded(true);
        } else if (!isLoading && !data) {
            // No saved filters found, initialize with defaults
            setFiltersLoaded(true);
        }
    }, [fetchFilters]);

    // Fetch simulations when user changes
    useEffect(() => {
        if (!user) return;

        const { url, method } = buildApiRequestConfig(routes.simulation.fetchByEntity, [{ param: 'entityId', value: user.entityId }]);
        setFetchSimulations({ url, method });
    }, [user]);

    // Only needed to sync toggles against an applied simulation
    // (syncTogglesFromSimulation below) - no point fetching it otherwise,
    // it's an expensive full-entity scan.
    useEffect(() => {
        if (!user || !simulationId) return;

        const { url, method } = buildApiRequestConfig(routes.transaction.summary, [{ param: 'entityId', value: user.entityId }]);
        setFetchSummary({ url, method });
    }, [user, simulationId]);

    // Handle loaded summary
    useEffect(() => {
        const { data, isLoading } = fetchSummary;
        if (data && !isLoading) {
            setSummaryData(data);
        }
    }, [fetchSummary]);

    // Helper: Sync toggles based on simulation exclusions
    const syncTogglesFromSimulation = React.useCallback((simId: string, simList: any[], summary: any) => {
        const foundSimulation = simList.find(sim => String(sim.id) === String(simId));
        if (!foundSimulation) return;

        // Only sync if we have valid summary data - don't sync with null/empty summary
        if (!summary) {
            return;
        }

        // Build complete list of all items by type from summary
        const allItemsByType = {
            real: new Set<string>(),
            recurring: new Set<string>(),
            oneTime: new Set<string>()
        };

        // Add real transactions
        if (summary.realTransactionsByBank) {
            Object.entries(summary.realTransactionsByBank).forEach(([_bankKey, bankData]: [string, any]) => {
                allItemsByType.real.add(`real-${bankData.bankAccountId}`);
            });
        }

        // Add recurring provisions
        if (summary.recurringProvisions) {
            const provisions = summary.recurringProvisions['Recurring Provision Templates']?.provisions || [];
            provisions.forEach((p: any) => {
                allItemsByType.recurring.add(String(p.id));
            });
        }

        // Add one-time provisions
        if (summary.oneTimeProvisions) {
            const provisions = summary.oneTimeProvisions['One-Time Provisions']?.provisions || [];
            provisions.forEach((p: any) => {
                allItemsByType.oneTime.add(`onetime-${p.id}`);
            });
        }

        // Collect what's excluded in the simulation
        const excludedByType = {
            real: new Set<string>(),
            recurring: new Set<string>(),
            oneTime: new Set<string>()
        };

        if (foundSimulation.simulationDetails) {
            foundSimulation.simulationDetails.forEach((detail: any) => {
                const id = detail.details?.id;
                if (id && detail.mode === 'exclude') {
                    // Skip invoices - they're not part of the three main categories
                    if (id === 'invoices') return;

                    if (id.startsWith('real-')) {
                        excludedByType.real.add(id);
                    } else if (id.startsWith('onetime-')) {
                        excludedByType.oneTime.add(id);
                    } else {
                        // Numeric IDs are recurring provisions
                        excludedByType.recurring.add(id);
                    }
                }
            });
        }

        // Check if ALL items of each type are excluded (only if items exist)
        const allRealExcluded = allItemsByType.real.size > 0 && allItemsByType.real.size === excludedByType.real.size;
        const allRecurringExcluded = allItemsByType.recurring.size > 0 && allItemsByType.recurring.size === excludedByType.recurring.size;
        const allOneTimeExcluded = allItemsByType.oneTime.size > 0 && allItemsByType.oneTime.size === excludedByType.oneTime.size;

        // Always update toggles to match simulation state
        setIsReal(!allRealExcluded);
        setIsRecurringProvisions(!allRecurringExcluded);
        setIsOneTimeProvisions(!allOneTimeExcluded);
    }, [summaryData]);

    // Handle loaded simulations
    useEffect(() => {
        const { data, isLoading } = fetchSimulations;
        if (data && !isLoading && Array.isArray(data)) {
            setSimulationsList(data);
            
            // If we have a simulation ID pending a name, try to find it from the newly loaded list
            if (simulationId && simulationId.id && !simulationId.name) {
                const foundSimulation = data.find(sim => String(sim.id) === String(simulationId.id));
                if (foundSimulation) {
                    setSimulationId({
                        id: simulationId.id,
                        name: foundSimulation.name
                    });
                }
            }

            // Immediately sync toggles if we have an active simulation
            // This ensures toggles update when simulation details are fetched (e.g., after modal save)
            if (simulationId && simulationId.id) {
                syncTogglesFromSimulation(simulationId.id, data, summaryData);
            }
        }
    }, [fetchSimulations, simulationId, summaryData, syncTogglesFromSimulation]);

    // Ensure simulation name is filled in whenever we have both ID and simulations list
    useEffect(() => {
        if (simulationId && simulationId.id && !simulationId.name && simulationsList.length > 0) {
            const foundSimulation = simulationsList.find(sim => String(sim.id) === String(simulationId.id));
            if (foundSimulation?.name) {
                setSimulationId({
                    id: simulationId.id,
                    name: foundSimulation.name
                });
            }
        }
    }, [simulationId?.id, simulationsList]);

    // Sync whenever simulationId changes (e.g., selecting an existing simulation)
    useEffect(() => {
        if (!simulationId || !simulationId.id || !summaryData) return;
        
        // If simulationDetails were passed from modal (newly created simulation), use them directly
        if (simulationId.simulationDetails && Array.isArray(simulationId.simulationDetails)) {
            const simWithDetails = {
                id: simulationId.id,
                simulationDetails: simulationId.simulationDetails
            };
            syncTogglesFromSimulation(simulationId.id, [simWithDetails], summaryData);
        } else {
            // Otherwise, try to find in the list
            const foundSimulation = simulationsList.find(sim => String(sim.id) === String(simulationId.id));
            
            // If not found, refetch simulations (for existing simulations not yet in list)
            if (!foundSimulation && user) {
                const { url, method } = buildApiRequestConfig(routes.simulation.fetchByEntity, [{ param: 'entityId', value: user.entityId }]);
                setFetchSimulations({ url, method });
                return; // Wait for refetch before syncing
            }
            
            // If found, sync
            if (foundSimulation) {
                syncTogglesFromSimulation(simulationId.id, simulationsList, summaryData);
            }
        }
    }, [simulationId?.id, simulationsList, summaryData, syncTogglesFromSimulation, user]);

    // Update favorite simulations with actual names from simulations list
    useEffect(() => {
        if (favoriteSimulations.length > 0 && simulationsList.length > 0) {
            const updatedFavorites = favoriteSimulations.map(fav => {
                // Check if the name is just the ID (fallback case from initial load)
                if (fav.name === fav.id || !fav.name) {
                    const foundSim = simulationsList.find(sim => String(sim.id) === String(fav.id));
                    if (foundSim?.name) {
                        return { id: fav.id, name: foundSim.name };
                    }
                }
                return fav;
            });
            
            // Only update if something actually changed
            if (JSON.stringify(updatedFavorites) !== JSON.stringify(favoriteSimulations)) {
                setFavoriteSimulations(updatedFavorites);
            }
        }
    }, [simulationsList]);

    // Save filters when they change (debounced, but not during initialization or drill-down)
    useEffect(() => {
        if (!filtersLoaded || isDrillDownRef.current || isInitialLoadRef.current) return;
        
        const timer = setTimeout(() => {
            saveFiltersDebounced();
        }, 1000); // 1 second debounce

        return () => clearTimeout(timer);
    }, [startDate, endDate, isReal, isRecurringProvisions, isOneTimeProvisions, viewBy, bank, category, unitValue, isDateRange, filtersLoaded, simulationId]);

    // Mark initial load as complete after all initialization is done
    useEffect(() => {
        if (filtersLoaded && categoriesLoaded && banksLoaded) {
            // Wait a tick to allow filter state updates to complete
            const timer = setTimeout(() => {
                isInitialLoadRef.current = false;
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [filtersLoaded, categoriesLoaded, banksLoaded]);

    const handleCategoryChange = React.useCallback((newCategories: string[]) => {
        setCategory(newCategories);
        const processedCategories = newCategories.filter(id => !['select-all-categories'].includes(id));
        
        setFilterConfig(prev => {
            if (processedCategories.length > 0) {
                return {
                    ...prev,
                    sortBy: prev?.sortBy ?? 'date',
                    sortDirection: prev?.sortDirection || 'DESC',
                    categoryIds: processedCategories
                };
            } else {
                if (prev && prev.categoryIds) {
                    const { categoryIds, ...rest } = prev;
                    return {
                        ...rest,
                        sortBy: rest.sortBy ?? 'date',
                        sortDirection: rest.sortDirection || 'DESC'
                    };
                }
                return prev;
            }
        });
    }, []);

    const handleFilterConfigChange = React.useCallback((value: React.SetStateAction<FilterParams | undefined>) => {
        let newFilterConfig: FilterParams | undefined;
        if (typeof value === 'function') {
            newFilterConfig = value(filterConfig);
        } else {
            newFilterConfig = value;
        }
        
        setFilterConfig(newFilterConfig);
        if (newFilterConfig?.categoryIds) {
            setCategory(newFilterConfig.categoryIds);
        } else {
            setCategory([]);
        }
    }, [filterConfig]);

    // Provision handlers
    const handleCreateProvision = React.useCallback((newProvision: Provision) => {
        const { url, method } = buildApiRequestConfig(routes.provisionTemplate.create);
        const body: any = { ...newProvision, entityId: user?.entityId, appliedTaxBreakdown: JSON.stringify(newProvision.appliedTaxBreakdown), isDescriptionOnlyMatch };
        if (descriptionOptions) {
            body.descriptionOptions = descriptionOptions;
        }
        // Only present for a "Provision This" flow (analysisData is set from
        // analyzeTransactionPatterns's response) - a plain manual Add
        // Provision never populates this, so the template's
        // patternAnalysisSnapshot correctly stays null for those.
        if ((newProvision as any).provisionThisSourceGuid && analysisData) {
            body.patternAnalysisSnapshot = {
                interval: analysisData.interval,
                medianDays: analysisData.medianDays,
                matchedRange: analysisData.matchedRange,
                usedTolerancePercent: analysisData.usedTolerancePercent,
                transactions: analysisData.allMatchingTransactions,
            };
        }
        setCreateProvision({ url, method, body });
    }, [user?.entityId, setCreateProvision, isDescriptionOnlyMatch, descriptionOptions, analysisData]);

    const handleUpdateProvision = React.useCallback((newProvision: Provision) => {
        if (!newProvision.id) return;
        const { url, method } = buildApiRequestConfig(routes.provisionTemplate.update, [{ param: 'id', value: newProvision.id }]);
        setUpdateProvision({ url, method, body: { ...newProvision, entityId: user?.entityId, appliedTaxBreakdown: JSON.stringify(newProvision.appliedTaxBreakdown) } });
    }, [user?.entityId, setUpdateProvision]);

    // Transaction handlers
    const handleCreateTransaction = React.useCallback((newTransaction: any) => {
        const { url, method } = buildApiRequestConfig(routes.transaction.create);
        setCreateTransaction({ 
            url, 
            method, 
            body: { 
                entityId: user?.entityId, 
                description: newTransaction.description, 
                amount: Number(newTransaction.amount), 
                date: newTransaction.transactionDate, 
                categoryId: newTransaction.categoryId,
                bankAccountId: newTransaction.bankAccountId
            } 
        });
    }, [user?.entityId, setCreateTransaction]);

    // The add-transaction form doubles as the edit form for manually-added
    // transactions - it's an update when one is being edited, otherwise a create.
    // (No entityId in the update body: the server resolves permission from the record.)
    const handleSaveTransaction = React.useCallback((tx: any) => {
        if (!manualEditTarget) {
            handleCreateTransaction(tx);
            return;
        }
        const { url, method } = buildApiRequestConfig(routes.transaction.updateManual, [{ param: 'guid', value: manualEditTarget.id }]);
        setUpdateTransaction({
            url,
            method,
            body: {
                description: tx.description,
                amount: Number(tx.amount),
                date: tx.transactionDate,
                categoryId: tx.categoryId ?? null,
                bankAccountId: tx.bankAccountId ?? null,
            },
        });
    }, [manualEditTarget, handleCreateTransaction, setUpdateTransaction]);

    // Handler for updating transaction category from dropdown
    const handleUpdateTransactionCategory = React.useCallback((transactionGuid: string, categoryId: number | null, applyDefaultTaxes: boolean) => {
        const { url, method } = buildApiRequestConfig(routes.transaction.updateCategory, [{ param: 'guid', value: transactionGuid }]);
        setUpdateTransactionCategory({
            url,
            method,
            body: { categoryId, applyDefaultTaxes }
        });
    }, [setUpdateTransactionCategory]);

    // Handler for logout
    const handleLogout = React.useCallback(() => {
        // Ends the session on the server too - before the cookie holding the token goes.
        endServerSession();
        Cookies.remove('token');
        Cookies.remove('entities');
        Cookies.remove('selectedCompany');
        navigate('/login');
    }, [navigate]); 

    useEffect(() => {
        document.title = "FineAnts | Transactions";
        const { url: categoryUrl, method: categoryMethod } = buildApiRequestConfig(routes.categories.fetchByEntity, [{ param: 'entityId', value: user?.entityId }]);
        const { url: bankUrl, method: bankMethod } = buildApiRequestConfig(routes.bankAccounts.fetchByEntity, [{ param: 'entityId', value: user?.entityId }]);
        setFetchCategories({ url: categoryUrl, method: categoryMethod });
        setFetchBanks({ url: bankUrl, method: bankMethod });
        if (user?.entityId) {
            const { url: taxUrl, method: taxMethod } = buildApiRequestConfig(routes.taxDefinitions.fetchByEntity, [{ param: 'entityId', value: user.entityId }]);
            setFetchTaxDefs({ url: taxUrl, method: taxMethod });
        }
    }, [user]);    

    // Column widths/visibility depend on viewBy alone - kept as its own
    // effect (not bundled with the fetch effect below) since it's a pure UI
    // concern that must still react to viewBy even though viewBy no longer
    // triggers a server fetch for the common (non-simulation) case.
    useEffect(() => {
        if (viewBy === 'transactions') {
            setColumns(defaultColumns);
        } else {
            const newColumns = defaultColumns.filter(col => !ICON_COLUMN_IDS.has(col.id) && col.id !== 'description' && col.id !== 'category' && col.id !== 'bank' && !col.id.startsWith('tax_'));
            // Make date column wider for week view
            if (viewBy === 'week') {
                const updatedColumns = newColumns.map(col =>
                    col.id === 'date' ? { ...col, width: 140 } : col
                );
                setColumns(updatedColumns);
            } else if (viewBy === 'month' || viewBy === 'quarter') {
                const updatedColumns = newColumns.map(col =>
                    col.id === 'date' ? { ...col, width: 80 } : col
                );
                setColumns(updatedColumns);
            } else {
                setColumns(newColumns);
            }
        }
    }, [viewBy, defaultColumns]);

    // Simulation stays fully server-computed (its own display filters/sort/
    // group - see transaction-controller.js), so date range/viewBy/
    // filterConfig changes must still refetch while a simulation is active.
    // For the common (non-simulation) case those are purely client-side
    // derivations now, so they must NOT trigger a refetch - these guarded
    // deps collapse to a constant `null` when there's no simulation, so
    // changing the underlying state doesn't change what the effect below
    // depends on.
    const simDateDep = simulationId ? startDate.getTime() : null;
    const simEndDateDep = simulationId ? endDate.getTime() : null;
    const simViewByDep = simulationId ? viewBy : null;
    const simFilterConfigDep = simulationId ? JSON.stringify(filterConfig) : null;

    // Extracted to a plain callback (not just inline in the effect below) so
    // a delete's success handler can call it directly and synchronously,
    // instead of only bumping refreshTrigger and hoping the effect below
    // notices in time - removes a layer of dependency-array indirection from
    // the one path (bulk delete) where staleness actually got reported.
    const fetchTransactions = React.useCallback(() => {
        const processedBankIds = bank.filter(id => !['select-all-banks'].includes(id)).map(id => {
            if (id === 'no-bank-account') {
                return 'null';
            }
            return id;
        });

        const processedCategoryIds = category.filter(id => !['select-all-categories'].includes(id));

        setData([]); // Clear existing data
        setStage('fetching'); // Show loading state

        const params = [
            { param: 'entityId', value: user?.entityId },
        ];

        const body: TransactionBody = filterDefaultsResolvedRef.current ? {
            showReal: isReal,
            showRecurring: isRecurringProvisions,
            showOneTime: isOneTimeProvisions,
            bankIds: processedBankIds.length > 0 ? processedBankIds.join(',') : undefined,
            categoryIds: processedCategoryIds.length > 0 ? processedCategoryIds.join(',') : undefined,
            simulationId: simulationId?.id || undefined,
            // Only meaningful server-side for simulation (see
            // transaction-controller.js) - sent regardless, harmlessly
            // ignored server-side otherwise.
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0],
            viewBy: viewBy,
            filterConfig: filterConfig ? JSON.stringify(filterConfig) : undefined,
        } : {
            useSavedFilters: true,
            simulationId: simulationId?.id || undefined,
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0],
            viewBy: viewBy,
            filterConfig: filterConfig ? JSON.stringify(filterConfig) : undefined,
        };

        const { url, method } = buildApiRequestConfig(routes.transaction.fetchByEntity, params);

        setFetch({ url, method, body });
    }, [bank, category, user, isReal, isRecurringProvisions, isOneTimeProvisions, simulationId, startDate, endDate, viewBy, filterConfig, setFetch]);

    // Everything that defines WHAT is being viewed. When a refetch runs with
    // none of these changed, it was caused by an action (edit, delete, link,
    // import, provision created, ...) - the user should stay where they were
    // scrolled, not be thrown back to today. A changed view is a fresh
    // navigation and still lands on today.
    const viewKey = JSON.stringify([
        user?.entityId, isReal, isRecurringProvisions, isOneTimeProvisions, bank, category,
        simulationId?.id ?? null, startDate?.toISOString?.(), endDate?.toISOString?.(), viewBy, filterConfig ?? null,
    ]);
    const lastFetchedViewKeyRef = React.useRef<string | null>(null);

    useEffect(() => {
        if (!initialLoadComplete) return;
        if (skipNextFetchRef.current) {
            skipNextFetchRef.current = false;
            return;
        }
        if (lastFetchedViewKeyRef.current === viewKey) {
            transactionsTableRef.current?.captureScrollAnchor();
        }
        lastFetchedViewKeyRef.current = viewKey;
        fetchTransactions();
        // deleteTransaction is deliberately NOT a dependency here - see the
        // comment in its own success effect above. refreshTrigger (bumped
        // there on genuine success only) is what actually drives the
        // post-delete refetch now, alongside a direct fetchTransactions()
        // call from that same handler.
    }, [user, isReal, isRecurringProvisions, isOneTimeProvisions, bank, category, doneImporting, createProvision, initialLoadComplete, refreshTrigger, markTrx, simulationId, simDateDep, simEndDateDep, simViewByDep, simFilterConfigDep, fetchTransactions]);

    useEffect(() => {
        const { data, isLoading, error } = fetchCategories;
        if (data && !isLoading) {
            // Guard against a malformed/unexpected response shape - every
            // downstream memo below (hierarchicalCategoryOptions,
            // categoryDefaultTaxIdsMap, flatCategoryDropdownOptions) calls array
            // methods directly on this with no fallback of its own.
            setHierarchicalCategories(Array.isArray(data) ? data : []);
            setCategoriesLoaded(true);
        }
        if (error) {
            showError(error ? error.toString() : "Failed to fetch categories");
        }
    }, [fetchCategories]);

    useEffect(() => {
        const { data, isLoading, error } = fetchBanks;
        if (data && !isLoading) {
            setRawBanks(data);
            setBanks(data.map((bank: any) => ({ value: bank.id, label: `${bank.bank} (${bank.name})` })));
            setBanksLoaded(true);
        } 
        if (error) {
            showError(error ? error.toString() : "Failed to fetch banks");
        }
    }, [fetchBanks]);

    useEffect(() => {
        const { data } = fetchTaxDefs;
        if (!data) return;
        const list: any[] = Array.isArray(data) ? data : data.taxDefinitions ?? [];
        setTaxOptions(list.filter((t: any) => t.isActive && t.isEnabled && t.showInTransactions).map((t: any) => ({ id: t.id, name: t.name, rate: t.rate })));
    }, [fetchTaxDefs]);

    useEffect(() => {
        if (viewBy === 'transactions') {
            setColumns(defaultColumns);
        }
    }, [defaultColumns]);

    const handleUpdateTransactionTax = React.useCallback((transactionGuid: string, appliedTaxIds: number[]) => {
        const { url, method } = buildApiRequestConfig(routes.transaction.updateTax, [{ param: 'guid', value: transactionGuid }]);
        setUpdateTransactionTax({ url, method, body: { appliedTaxIds } });
    }, [setUpdateTransactionTax]);

    const handleConfirmChangeDate = React.useCallback((guid: string, newDate: Dayjs) => {
        const { url, method } = buildApiRequestConfig(routes.transaction.updateDate, [{ param: 'guid', value: guid }]);
        setUpdateProvisionDate({ url, method, body: { date: newDate.toISOString() } });
    }, [setUpdateProvisionDate]);

    // Doesn't wait on filters/categories/banks - the transaction fetch
    // resolves the user's saved filter server-side (useSavedFilters below),
    // and categories/banks only feed dropdown option labels, not the fetch
    // itself. Only real prerequisite is knowing which entity to fetch.
    useEffect(() => {
        if (user?.entityId) {
            setStage('fetching');
            setInitialLoadComplete(true);
        }
    }, [user?.entityId]);

    useEffect(() => {
        const { data, isLoading, error } = updateTransactionTax;
        if (data && !isLoading) {
            showSuccess("Tax updated successfully");
        }
        if (error) {
            showError(error ? error.toString() : "Failed to update tax");
        }
    }, [updateTransactionTax]);

    useEffect(() => {
        const { data, isLoading, error } = updateProvisionDate;
        if (data && !isLoading) {
            showSuccess("Provision date updated successfully");
            setChangeDateTarget(null);
            // Date changes affect sort order/running balance/the "moved
            // back" highlight - a full refetch is simplest and correct,
            // unlike category/tax which just patch the row in place.
            // Keep the user's scroll position across this refresh (see captureScrollAnchor).
            transactionsTableRef.current?.captureScrollAnchor();
            setRefreshTrigger(prev => prev + 1);
        }
        if (error) {
            showError(error ? error.toString() : "Failed to update provision date");
        }
    }, [updateProvisionDate]);

    useEffect(() => {
        const { data, isLoading, error } = deleteTransaction;
        if (data && !isLoading) {
            showSuccess("Transaction deleted successfully");
            setOpenModal('');
            // deleteTransaction used to sit directly in the big fetch effect's
            // dependency array below - but useApi's result object changes
            // reference on BOTH the loading->true transition (fired the
            // instant the delete request is sent, before anything is
            // actually deleted yet) and the loading->false success
            // transition, so that refetched the list twice per delete: once
            // uselessly early with pre-delete data, once correctly after.
            // Usually harmless, but back-to-back deletes (e.g. "delete this
            // and future" immediately followed by "delete all", the exact
            // sequence that surfaced this) could let the premature refetch
            // from delete #2 resolve after the correct one and overwrite it
            // with stale data, so the list wouldn't reflect the last delete
            // without a manual page refresh. refreshTrigger only bumps once,
            // on genuine success, matching every other action handler here.
            // Keep the user's scroll position across this refresh (see captureScrollAnchor).
            transactionsTableRef.current?.captureScrollAnchor();
            setRefreshTrigger(prev => prev + 1);
            // Also call it directly rather than relying solely on the
            // refreshTrigger bump above reaching the fetch effect - this is
            // the one action where staleness was actually observed, so don't
            // depend on a second layer of effect-dependency timing to fix it.
            fetchTransactions();
            // Reset here (once the modal is actually closing), not in
            // onConfirm - see the comment there.
            setIsRecurringProvisioning(false);
            setDeleteOption('this-occurrence');
            setDeleteIsManual(false);
        }
        if (error) {
            showError(error ? error.toString() : "Failed to delete transaction");
        }
    }, [deleteTransaction, fetchTransactions]);

    useEffect(() => {
        const { data, isLoading, error } = terminate;
        if (data && !isLoading) {
            showSuccess("Provision terminated successfully");
            setOpenModal('');
            setTerminateTarget(null);
            setTerminateEndDate('');
            // Occurrences after the chosen end date are removed - a full
            // refetch is simplest and correct, same as a date change.
            // Keep the user's scroll position across this refresh (see captureScrollAnchor).
            transactionsTableRef.current?.captureScrollAnchor();
            setRefreshTrigger(prev => prev + 1);
        }
        if (error) {
            showError(error ? error.toString() : "Failed to terminate provision");
        }
    }, [terminate]);

    useEffect(() => {
        const { data, isLoading, error } = createTransaction;
        if (data && !isLoading) {
            showSuccess("Transaction created successfully");
            setOpenModal('');
            // Keep the user's scroll position across this refresh (see captureScrollAnchor).
            transactionsTableRef.current?.captureScrollAnchor();
            setRefreshTrigger(prev => prev + 1);
        }
        if (error) {
            showError(error ? error.toString() : "Failed to create transaction");
        }
    }, [createTransaction]);

    useEffect(() => {
        const { data, isLoading, error } = updateTransaction;
        if (data && !isLoading) {
            showSuccess("Transaction updated successfully");
            setOpenModal('');
            setManualEditTarget(null);
            // Keep the user's scroll position across this refresh (see captureScrollAnchor).
            transactionsTableRef.current?.captureScrollAnchor();
            setRefreshTrigger(prev => prev + 1);
        }
        if (error) {
            showError(error ? error.toString() : "Failed to update transaction");
        }
    }, [updateTransaction]);

    useEffect(() => {
        const { data: provisionResponse, isLoading, error } = createProvision;
        // useApi's setXxx(null) only clears the request config, never the previous
        // response (result.data) - so provisionResponse stays truthy indefinitely
        // after a successful call, and this effect re-evaluates its condition on
        // every later render this hook instance is involved in (e.g. re-renders
        // triggered by other state changes). Without this guard, that stale
        // response gets reprocessed and pops the modal back open long after the
        // original creation actually finished.
        if (provisionResponse && !isLoading && provisionResponse?.data?.id && lastShownProvisionSuccessIdRef.current !== provisionResponse.data.id) {
            lastShownProvisionSuccessIdRef.current = provisionResponse.data.id;
            console.log('[ProvisionThis] createProvision response:', provisionResponse, 'provisionTheseSourceTransactions.length:', provisionTheseSourceTransactions.length);
            setOpenModal('');
            setIsDescriptionOnlyMatch(false);
            setDescriptionOptions(null);
            showSuccess("Provision created successfully");

            // Provision These: show modal with frontend-tracked sources if present
            if (provisionTheseSourceTransactions.length > 0) {
                setCreatedProvisionIdBoth(provisionResponse.data.id);
                setProvisionTransactions(provisionTheseSourceTransactions);
                setShowProvisionTransactions(true);
                setProvisionTheseSourceTransactions([]);
            } else if (Array.isArray(provisionResponse.transactionsToMark) && provisionResponse.transactionsToMark.length > 0) {
                // Single source "Provision This": show exactly what the backend itself
                // matched and already marked (transactionsToMark, returned straight from
                // createProvisionTemplate) - not a separately-computed frontend snapshot,
                // which could diverge from what was actually marked.
                console.log('[ProvisionThis] showing review modal, transactionsToMark:', provisionResponse.transactionsToMark);
                setCreatedProvisionIdBoth(provisionResponse.data.id);
                setProvisionTransactions(provisionResponse.transactionsToMark);
                setShowProvisionTransactions(true);
            } else {
                console.log('[ProvisionThis] NOT showing review modal - transactionsToMark:', provisionResponse?.transactionsToMark);
            }

            setAnalysisData(null);
            setCreateProvision(null);
        }
        if (error) {
            showError(error ? error.toString() : "Failed to create provision");
            setCreateProvision(null);
        }
    }, [createProvision, provisionTheseSourceTransactions]);

    useEffect(() => {
        const { data, isLoading, error } = updateProvision;
        if (data && !isLoading) {
            showSuccess("Provision updated successfully");
            setOpenModal('');
            // Keep the user's scroll position across this refresh (see captureScrollAnchor).
            transactionsTableRef.current?.captureScrollAnchor();
            setRefreshTrigger(prev => prev + 1);
        }
        if (error) {
            showError(error ? error.toString() : "Failed to update provision");
        }
    }, [updateProvision]);

    useEffect(() => {
        const { data, isLoading, error } = updateTransactionCategory;
        if (data && !isLoading) {
            showSuccess("Category updated successfully");
            // Patch just this row locally instead of refetching the whole
            // list - the backend already returns the fields that changed
            // (category, and appliedTaxIds if defaults were applied).
            const updated = data.data;
            if (updated?.guid) {
                setData(prev => prev.map(row => row.id === updated.guid
                    ? { ...row, category: updated.categoryId || '', appliedTaxIds: updated.appliedTaxIds ?? row.appliedTaxIds }
                    : row
                ));
                // Same patch on the source rows the table data is rebuilt
                // from - otherwise the next rebuild (balance/filter/view
                // change) put the old category back.
                setRawTransactions(prev => prev.map((tx: any) => tx.guid === updated.guid
                    ? { ...tx, categoryId: updated.categoryId ?? null, appliedTaxIds: updated.appliedTaxIds ?? tx.appliedTaxIds }
                    : tx
                ));
            }
        }
        if (error) {
            showError(error ? error.toString() : "Failed to update category");
        }
    }, [updateTransactionCategory]);

    useEffect(() => {
        const { data, isLoading, error } = markTrx;
        // Same useApi quirk as createProvision above: setMarkTrx(null) only clears
        // the request config, not the previous response, so guard against
        // reprocessing the same already-handled response on a later re-render.
        if (data && !isLoading && processedMarkTrxRef.current !== data) {
            processedMarkTrxRef.current = data;
            showSuccess("Transactions marked as provisioned successfully");
            setShowProvisionTransactions(false);
            setCreatedProvisionIdBoth(null);
            setProvisionTransactions([]);
            setProvisionTheseSourceTransactions([]);
            // Keep the user's scroll position across this refresh (see captureScrollAnchor).
            transactionsTableRef.current?.captureScrollAnchor();
            setRefreshTrigger(prev => prev + 1);
            setMarkTrx(null);
        }
        if (error) {
            showError(error ? error.toString() : "Failed to mark transactions");
            setMarkTrx(null);
        }
    }, [markTrx, showSuccess, showError]);

    useEffect(() => {
        const { data, isLoading, error } = createCompany;
        if (data && !isLoading && data !== lastCreateCompanyDataRef.current) {
            lastCreateCompanyDataRef.current = data;
            showSuccess("Company created successfully");
            
            // Update the entities cookie with the newly created company
            const currentEntities = Cookies.get('entities');
            const entities = currentEntities ? JSON.parse(currentEntities) : [];
            
            // Add the new company to the list
            if (data) {
                entities.push(data);
                Cookies.set('entities', JSON.stringify(entities), { expires: 365 });
            }
            
            // Set it as the selected company and update user
            if (data?.entityId) {
                Cookies.set('selectedCompany', data.entityId.toString(), { expires: 365 });
                
                // Update user cookie and context with new entityId
                const currentUser = Cookies.get('user');
                if (currentUser) {
                    const userObj = JSON.parse(currentUser);
                    userObj.entityId = data.entityId;
                    Cookies.set('user', JSON.stringify(userObj), { expires: 365 });
                    setUser(userObj);
                }
            }
            
            setOpenAddEntity(false);
            setSearchParams(prev => {
                prev.delete('createCompany');
                return prev;
            });
            setRefreshTrigger(prev => prev + 1);
        }
        if (error) {
            showError(error ? error.toString() : "Failed to create company");
        }
    }, [createCompany, setSearchParams, setUser]);

    useEffect(() => {
        const { data: responseData, isLoading, error } = fetch;
        if (!isLoading && responseData) {
            setRawTransactions(responseData?.transactions ?? []);
            setTotalOpeningBalance(Number(responseData?.totalOpeningBalance) || 0);

            const status = responseData?.status || {};
            // Convert date strings to Date objects
            if (status.earliestTransaction && typeof status.earliestTransaction === 'string') {
                status.earliestTransaction = new Date(status.earliestTransaction);
            }
            if (status.latestTransaction && typeof status.latestTransaction === 'string') {
                status.latestTransaction = new Date(status.latestTransaction);
            }
            if (status.startOfRecords && typeof status.startOfRecords.date === 'string') {
                status.startOfRecords.date = new Date(status.startOfRecords.date);
            }
            if (status.endOfHorizon && typeof status.endOfHorizon === 'string') {
                status.endOfHorizon = new Date(status.endOfHorizon);
            }

            setCompanyStatus(prev => ({ ...prev, ...status }));
            setStage('complete');

            if (responseData?.resolvedFilters && !filterDefaultsResolvedRef.current) {
                filterDefaultsResolvedRef.current = true;
                skipNextFetchRef.current = true;
                const rf = responseData.resolvedFilters;
                setIsReal(rf.showReal !== false);
                setIsRecurringProvisions(rf.showRecurring !== false);
                setIsOneTimeProvisions(rf.showOneTime !== false);
                const bankIds = rf.bankIds?.split(',') || [];
                setBank(bankIds.map((id: string) => (id === 'null' || id === '') ? 'no-bank-account' : id));
                setCategory(rf.categoryIds?.split(',') || []);
            }
        }
        if (error) {
            showError(error ? error.toString() : "Failed to fetch transactions");
            setStage('complete');
        }
    }, [fetch]);

    /* SUPERSEDED - fetched a separate cash-flow-status endpoint response;
       now derived client-side (see the recompute effect below and
       computeCashFlowStatusClient in util/transaction-calculations.ts).
       Kept commented out for reference/rollback.

    useEffect(() => {
        const { data: responseData, isLoading, error } = fetchCompanyStatus;
        if (!isLoading && responseData) {
            setCompanyStatus({ ...companyStatus, ...responseData, cashflowMessage: responseData?.cashFlowMessage || 'Failed to load' });
        }
        if (error) {
            showError(error ? error.toString() : "Failed to fetch company status");
        }
    }, [fetchCompanyStatus]);
    */

    // Derives the displayed rows + cash-flow status client-side from the raw
    // fetch above (see util/transaction-calculations.ts) - this is what used
    // to be recomputed server-side on every date-range/sort/group/pagination
    // request. Simulation rows arrive already filtered/sorted/grouped from
    // the server (its rules engine isn't portable to the client - see
    // transaction-controller.js), so row ORDER/membership passes through
    // as-is - but balance is always computed here via computeRunningBalance,
    // the same single implementation used for the no-simulation case, rather
    // than trusting a second server-side balance walk. That second walk used
    // to drift from what "no simulation" would produce, which is exactly
    // what let an EMPTY simulation (no rules, nothing actually changed)
    // shift the reported "runs out of money" date versus no simulation at
    // all - computing it one way, from the same combined row set, removes
    // that class of bug entirely instead of just patching the discrepancy
    // found this time.
    useEffect(() => {
        // The perf-diagnostic toggle is meant to only skip the CLIENT's own
        // balance walk (see its declaration comment: "does not affect
        // simulation rows, which always arrive already balanced from the
        // server regardless"). That held for the per-row `balance` values
        // via the `?? tx.balance` fallback below, but not for cashFlowStatus
        // - it was unconditionally nulled out whenever the toggle was off,
        // which left the header notification stuck on "Loading..." forever
        // during a simulation with the toggle off, even though every row's
        // own Cash Position column was showing real numbers.
        const skipBalance = !cashPositionEnabled && !simulationId;

        const transactionsWithBalance = skipBalance
            ? rawTransactions.map((tx: any) => ({ ...tx, balance: null }))
            : computeRunningBalance(rawTransactions, totalOpeningBalance, companyStatus.includePushedForwardInBalance ?? true);

        const cashFlowStatus = skipBalance ? null : computeCashFlowStatusClient(transactionsWithBalance, totalOpeningBalance);

        let finalRows: any[];
        let highLowSource: any[];
        if (simulationId) {
            // Keep the server's row order/grouping (its rules engine isn't
            // portable to the client), but use the balance just computed
            // above instead of whatever the server sent, so simulated rows
            // are guaranteed consistent with the no-simulation calculation.
            const balanceByGuid = new Map(transactionsWithBalance.map((tx: any) => [tx.guid, tx.balance]));
            const excludedByGuid = new Map(transactionsWithBalance.map((tx: any) => [tx.guid, tx.excludedFromBalance]));
            finalRows = rawTransactions.map((tx: any) => ({ ...tx, balance: balanceByGuid.get(tx.guid) ?? tx.balance, excludedFromBalance: excludedByGuid.get(tx.guid) ?? false }));
            highLowSource = finalRows;
        } else {
            // Bank account names (institution + account name) for the header search.
            const bankNames = new Map<string, string>(rawBanks.map((b: any) => [b.id, `${b.bank} ${b.name}`]));
            const displayTransactions = applyDisplayFilters(transactionsWithBalance, {
                startDate, endDate, filterConfig: filterConfig as any, bankNames,
            });
            highLowSource = displayTransactions;
            finalRows = (viewBy && viewBy !== 'transactions')
                ? groupTransactionsClient(displayTransactions, viewBy as any, companySettings?.settings?.startDayOfWeek, filterConfig?.sortDirection || 'DESC')
                : sortTransactions(displayTransactions, filterConfig as any);
        }

        const { highestAmount, lowestAmount } = computeHighLow(highLowSource);

        const transactions = finalRows.map((tx: any) => ({
            id: tx.guid,
            date: tx.date,
            amount: tx.amount,
            balance: tx.balance,
            excludedFromBalance: tx.excludedFromBalance === true,
            description: tx.description,
            originalDescription: tx.originalDescription,
            bank: tx.bankAccountId || 'No Bank Account',
            type: tx.type,
            category: tx.categoryId || '',
            appliedTaxIds: tx.appliedTaxIds ?? [],
            provisionId: tx.provisionTemplateId || null,
            invoiceNumber: tx.invoiceNumber || '',
            isRecurring: tx.isRecurring,
            isProvisioned: tx.isProvisioned || false,
            provisionThisTemplateId: tx.provisionThisTemplateId || null,
            linkedTransactionId: tx.linkedTransactionId || null,
            // A real transaction that a provision links TO never has its own
            // linkedTransactionId set (only the provision side stores it) -
            // this reverse-direction flag is computed server-side so the
            // "linked" icon shows for both sides of the relationship.
            hasLinkedTransactions: tx.hasLinkedTransactions || false,
            // Matches transaction-detail.tsx's own fallback for consistency
            // between the row-level "Link a Provision" action and the modal's
            // OPEN/CLOSED toggle.
            isLinkable: tx.isLinkable ?? false,
            isSimulation: tx.isSimulation || false,
            staffPaymentId: tx.staffPaymentId || null,
            // Invoice was already PAID in Xero at the moment it was imported
            // (see buildProvisionRow in xero-import-service.js) - display-only,
            // stays type: 'provision' so linking/reconciliation is unaffected.
            // Only borrow real-transaction styling while genuinely unlinked -
            // once xero-import-service.js links this provision to the real
            // transaction that paid it, the provision styling (and the
            // "linked" icon) needs to actually show, same reasoning as
            // isPaidAtImportProvision in transaction-detail.tsx.
            paidAtImport: tx.sourceData?.paidAtImport === true && !tx.hasLinkedTransactions,
            // Category gets overwritten during CSV<->Xero reconciliation
            // (importBankTransactions/importInvoices in xero-import-service.js)
            // - the pre-overwrite category is preserved in
            // sourceData.overriddenByXero, viewable in the detail popup.
            // description itself is never overwritten (dedup matching on
            // repeat syncs depends on it staying stable) - Xero's version is
            // a display-only overlay in sourceData.xeroDescription instead,
            // which applyXeroDescriptionOverlay (transaction-controller.js)
            // already swaps into `description` above before this reaches
            // the client, with the stable value exposed as originalDescription.
            reconciledWithXero: tx.sourceData?.overriddenByXero != null || tx.sourceData?.xeroDescription != null,
            xeroTransactionId: tx.xeroTransactionId || null,
            pushedForwardForAging: tx.sourceData?.pushedForwardForAging === true,
            // Only set by createTransaction (the "+ Transaction" button) -
            // imports never carry it, so they stay read-only.
            isManual: tx.type === 'real' && tx.sourceData?.manual === true,
            // Set by updateProvisionDate (Change Date, either direction) or
            // by the aging job pushing this provision forward again after a
            // prior manual edit - see transaction-controller.js.
            dateAdjustedBack: tx.sourceData?.dateAdjustedBack === true,
            originalDate: tx.sourceData?.originalDate || null,
        }));

        setData(transactions);
        setCompanyStatus(prev => ({
            ...prev,
            cashflowMessage: cashFlowStatus?.cashflowMessage ?? '',
            wentNegative: cashFlowStatus?.wentNegative ?? false,
            daysBeforeNegative: cashFlowStatus ? String(cashFlowStatus.daysBeforeNegative) : undefined,
            firstNegativeDate: cashFlowStatus?.firstNegativeDate ? new Date(cashFlowStatus.firstNegativeDate) : undefined,
            firstNegativeTransaction: cashFlowStatus?.firstNegativeTransaction ?? undefined,
            highestAmount,
            lowestAmount,
        }));
    }, [rawTransactions, totalOpeningBalance, simulationId, startDate, endDate, filterConfig, viewBy, companySettings, cashPositionEnabled, companyStatus.includePushedForwardInBalance, rawBanks]);

    const handleConfirmImport = async (approvedEntries: ImportRow[], importId: string) => {
        const CHUNK_SIZE = 100;
        const chunks = [];
        
        for (let i = 0; i < approvedEntries.length; i += CHUNK_SIZE) {
            chunks.push(approvedEntries.slice(i, i + CHUNK_SIZE));
        }
        
        setProgressModal({
            open: true,
            currentChunk: 0,
            totalChunks: chunks.length,
            processedTransactions: 0,
            totalTransactions: approvedEntries.length,
            status: 'processing'
        });
        
        let successCount = 0;
        let errorCount = 0;
        
        try {
            // Process chunks sequentially to avoid overwhelming the server
            for (let i = 0; i < chunks.length; i++) {
                const chunk = chunks[i];
                const { url, method } = buildApiRequestConfig(routes.transaction.bulkCreate);
                
                try {
                    // Make direct axios call instead of using useApi hook
                    const token = Cookies.get('token');
                    let headers: Record<string, string> = {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                    };

                    if (token) {
                        headers['Authorization'] = `Bearer ${token}`;
                    }

                    const response = await axios({
                        method,
                        url,
                        headers,
                        data: {
                            importId,
                            transactions: chunk
                        }
                    });
                    
                    if (response.status >= 200 && response.status < 300) {
                        successCount += chunk.length;
                        
                        // Update progress modal
                        const currentSuccessCount = successCount;
                        setProgressModal(prev => ({
                            ...prev,
                            currentChunk: i + 1,
                            processedTransactions: currentSuccessCount
                        }));
                    } else {
                        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                    }
                    
                    // Add a small delay between chunks to avoid overwhelming the server
                    if (i < chunks.length - 1) {
                        await new Promise(resolve => setTimeout(resolve, 500));
                    }
                    
                } catch (error: any) {
                    errorCount += chunk.length;
                    const currentSuccessCount = successCount;
                    setProgressModal(prev => ({
                        ...prev,
                        currentChunk: i + 1,
                        processedTransactions: currentSuccessCount,
                        status: 'error'
                    }));
                }
            }
            
            // Final status update
            const finalStatus = errorCount === 0 ? 'success' : 'error';
            setProgressModal(prev => ({
                ...prev,
                status: finalStatus
            }));
            
            // Refetch bank options after import to include any newly created accounts
            const { url: bankUrl, method: bankMethod } = buildApiRequestConfig(routes.bankAccounts.fetchByEntity, [{ param: 'entityId', value: user?.entityId }]);
            setFetchBanks({ url: bankUrl, method: bankMethod });
            
            // Auto-close after 2 seconds and show final result
            setTimeout(() => {
                setProgressModal(prev => ({ ...prev, open: false }));
                setDoneImporting(prev => !prev);
                if (errorCount > 0) {
                    showError(`Import completed with errors: ${successCount} successful, ${errorCount} failed`);
                }
            }, 2000);
            
        } catch (error) {
            setProgressModal(prev => ({
                ...prev,
                status: 'error'
            }));
            setTimeout(() => {
                setProgressModal(prev => ({ ...prev, open: false }));
                showError(`Import failed: ${error}`);
            }, 2000);
        }
    };

    const reanalyzeWithDescriptionOnly = (sourceTrxGuid: string, start?: number, length?: number) => {
        let { url, method } = buildApiRequestConfig(routes.provisionTemplate.analyzePatternByDescription, [{ param: 'transactionGuid', value: sourceTrxGuid }]);
        // append optional query parameters for description matching
        const qp: Record<string, any> = {};
        if (start !== undefined) qp.start = start;
        if (length !== undefined) qp.length = length;
        if (Object.keys(qp).length > 0) {
            const query = new URLSearchParams(qp).toString();
            url = `${url}?${query}`;
        }
        setAnalyzePattern({ url, method});
    };

    const reanalyzeWithAI = (sourceTrxGuid: string) => {
        let { url, method } = buildApiRequestConfig(routes.provisionTemplate.analyzePatternByAI, [{ param: 'transactionGuid', value: sourceTrxGuid }]);
        setAnalyzePatternByAI({ url, method});
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleAnalysisMethodSelect = (sourceTrxGuid: string, method: 'description' | 'ai') => {
        if (method === 'description') {
            // Show description match settings
            setIsReanalyzeInProgress(true);
            reanalyzeCancelledRef.current = false;
            setPendingDescriptionGuid(sourceTrxGuid);
            setShowMatchSettings(true);
            setIsReanalyze(true);
        } else {
            // Use AI analysis
            reanalyzeWithAI(sourceTrxGuid);
        }
    };

    const handleProvisionThese = (transactionGuids: string[]) => {
        if (transactionGuids.length === 0) return;
        // Store selected transaction objects for later modal
        setProvisionTheseSourceTransactions(data.filter(trx => transactionGuids.includes(trx.id)));
        const { url, method } = buildApiRequestConfig(routes.provisionTemplate.analyzeMultiple);
        setAnalyzeMultiple({ url, method, body: { transactionGuids } });
    };

    // Clear analysis state when a new analysis starts loading
    useEffect(() => {
        if (analyzePattern.isLoading) {
            // New analysis is starting - clear old results to prevent showing stale data
            setAnalysisData(null);
            setSourceTransaction(null);
            setProvisionThisData(null);
            // Reset match type flag for fresh analysis
            setIsDescriptionOnlyMatch(false);
        }
    }, [analyzePattern.isLoading]);

    // Clear analysis state when multiple analysis starts loading
    useEffect(() => {
        if (analyzeMultiple.isLoading) {
            setAnalysisData(null);
            setSourceTransaction(null);
            setProvisionThisData(null);
            // Reset match type flag for fresh analysis
            setIsDescriptionOnlyMatch(false);
        }
    }, [analyzeMultiple.isLoading]);

    // Clear analysis state when AI analysis starts loading
    useEffect(() => {
        if (analyzePatternByAI.isLoading) {
            // New analysis is starting - clear old results to prevent showing stale data
            setAnalysisData(null);
            setSourceTransaction(null);
            setProvisionThisData(null);
            // Reset match type flag for fresh analysis
            setIsDescriptionOnlyMatch(false);
        }
    }, [analyzePatternByAI.isLoading]);

    // Handle analyzePattern API response
    useEffect(() => {
        const { data, isLoading, error } = analyzePattern;
        if (data && !isLoading) {
            // Check if reanalysis was cancelled by user
            if (reanalyzeCancelledRef.current) {
                // Reset the flag and clean up
                reanalyzeCancelledRef.current = false;
                setAnalyzePattern(null);
                return;
            }
            // Store analysis data and source transaction regardless of matches
            if (data.analysis) {
                setAnalysisData(data.analysis);
            }
            if (data.sourceTransaction) {
                setSourceTransaction(data.sourceTransaction);
            }
            
            // If this is from description-only re-analysis, mark it immediately
            if (isReanalyze) {
                setIsDescriptionOnlyMatch(true);
            }
            
            // Check if there are matches
            if (!data.hasMatches) {
                // No matches - show dialog in debug mode or confirmation in normal mode
                if (showDebug) {
                    // In debug mode, show the first analysis result without auto-triggering reanalysis
                    setIsReanalyzeInProgress(false);
                    setShowAnalysisDialog(true);
                } else {
                    // In normal mode (debug OFF), prompt user for description-match settings before reanalyzing
                    if (!isReanalyze) {
                        setIsReanalyzeInProgress(true);
                        reanalyzeCancelledRef.current = false;
                        setPendingDescriptionGuid(data.sourceTransaction?.guid || null);
                        setPendingDescriptionText(data.sourceTransaction?.description || '');
                        setShowMatchSettings(true);
                        return setIsReanalyze(true);
                    }
                    setIsReanalyzeInProgress(false);
                    // No matches and debug OFF - show confirmation dialog
                    setPatternAnalysisMessage(data.analysis?.message || "Unable to analyze pattern. Please fill in the details manually.");
                    setShowPatternConfirmation(true);
                    // Set default data for if they confirm
                    const sourceTransaction = data.sourceTransaction || {};
                    setSourceTransactionGuid(sourceTransaction?.guid);
                    setProvisionThisData({
                        date: data.analysis?.startDate || sourceTransaction.date,
                        amount: data.analysis?.amount || sourceTransaction.amount,
                        description: data.analysis?.simplifiedDescription || sourceTransaction.description,
                        sourceTransactionGuid: sourceTransaction?.guid
                    });
                }
            } else if (showDebug) {
                // Matches found, debug mode: show analysis dialog to allow user to
                // review results and optionally reanalyze
                setShowAnalysisDialog(true);
            } else {
                // Matches found, debug off: skip the review window and go straight
                // to the provision form - but if the pattern looks stale, the cutoff
                // warning still needs to interrupt with its own confirm step first,
                // since it won't otherwise ever be shown to the user.
                if (data.analysis?.cutoffWarning?.shouldWarn) {
                    pendingCutoffConfirmRef.current = { guid: data.sourceTransaction?.guid, analysis: data.analysis, source: data.sourceTransaction };
                    setShowCutoffConfirmDebugOff(true);
                } else {
                    proceedToProvisionForm(data.sourceTransaction?.guid, data.analysis, data.sourceTransaction);
                }
            }
            // Reset re-analyzeInProgress flag when analysis completes
            if (isReanalyze) {
                setIsReanalyzeInProgress(false);
            }
            // Reset analyzePattern state
            setAnalyzePattern(null);
        } else if (error && !isLoading) {
            // API failed - use fallback data and open modal
            showError("Failed to analyze pattern. Using transaction data instead.");
            setAnalysisData(null);
            setSourceTransaction(null);
            setOpenModal('provision');
            // Reset re-analyzeInProgress flag on error
            setIsReanalyzeInProgress(false);
            // Reset analyzePattern state
            setAnalyzePattern(null);
        }
    }, [analyzePattern, showError, setAnalyzePattern, showDebug]);

    // Handle analyzeMultiple API response
    useEffect(() => {
        const { data, isLoading, error } = analyzeMultiple;
        if (data && !isLoading) {
            // Store analysis data and source transactions
            if (data.analysis) {
                setAnalysisData(data.analysis);
            }
            if (data.sourceTransactions && data.sourceTransactions.length > 0) {
                setSourceTransaction(data.sourceTransactions);
                // For Provision These, do NOT set sourceTransactionGuid to avoid sending source in creation
                // setSourceTransactionGuid(data.sourceTransactions[0]?.guid);
            }
            
            // Check if there are matches
            if (!data.hasMatches) {
                // No matches found for multiple transactions
                if (showDebug) {
                    // In debug mode, show the analysis result
                    setShowAnalysisDialog(true);
                } else {
                    // In normal mode, show confirmation dialog
                    setPatternAnalysisMessage(data.analysis?.message || "Unable to analyze patterns for the selected transactions. Please fill in the details manually.");
                    setShowPatternConfirmation(true);
                    // Set default data for if they confirm - use analysis data from multiple sources
                    // For Provision These, do NOT set sourceTransactionGuid
                    // setSourceTransactionGuid(data.sourceTransactions?.[0]?.guid);
                    setProvisionThisData({
                        date: adjustStartDate(data.analysis?.startDate, data.analysis),
                        amount: data.analysis?.amount,
                        description: data.analysis?.simplifiedDescription,
                        // No sourceTransactionGuid for Provision These
                    });
                }
            } else {
                // Matches found
                // Show analysis dialog to allow user to review results and optionally reanalyze
                setShowAnalysisDialog(true);
            }
            
            // Reset analyzeMultiple state
            setAnalyzeMultiple(null);
        } else if (error && !isLoading) {
            // API failed - show error
            showError("Failed to analyze multiple patterns. Please try again.");
            setAnalysisData(null);
            setSourceTransaction(null);
            // Reset analyzeMultiple state
            setAnalyzeMultiple(null);
        }
    }, [analyzeMultiple, showError, setAnalyzeMultiple, showDebug]);

    // Handle analyzePatternByAI API response
    useEffect(() => {
        const { data, isLoading, error } = analyzePatternByAI;
        if (data && !isLoading) {
            // Check if analysis was cancelled by user
            if (reanalyzeCancelledRef.current) {
                // Reset the flag and clean up
                reanalyzeCancelledRef.current = false;
                setAnalyzePatternByAI(null);
                return;
            }
            // Store analysis data and source transaction
            if (data.analysis) {
                setAnalysisData(data.analysis);
            }
            if (data.sourceTransaction) {
                setSourceTransaction(data.sourceTransaction);
            }
            
            // Check if there are matches
            if (!data.hasMatches) {
                // No matches found
                if (showDebug) {
                    // In debug mode, show the analysis result
                    setShowAnalysisDialog(true);
                } else {
                    // In normal mode, show confirmation dialog
                    setPatternAnalysisMessage(data.analysis?.message || "AI analysis couldn't find related transactions. Please fill in the details manually.");
                    setShowPatternConfirmation(true);
                    // Set default data
                    const sourceTransaction = data.sourceTransaction || {};
                    setSourceTransactionGuid(sourceTransaction?.guid);
                    setProvisionThisData({
                        date: data.analysis?.startDate || sourceTransaction.date,
                        amount: data.analysis?.amount || sourceTransaction.amount,
                        description: data.analysis?.simplifiedDescription || sourceTransaction.description,
                        sourceTransactionGuid: sourceTransaction?.guid
                    });
                }
            } else {
                // Matches found - show analysis dialog
                setShowAnalysisDialog(true);
            }
            // Reset analyzePatternByAI state after all state updates
            setAnalyzePatternByAI(null);
        } else if (error && !isLoading) {
            // API failed
            showError("AI analysis failed. Using transaction data instead.");
            setAnalysisData(null);
            setSourceTransaction(null);
            setOpenModal('provision');
            // Reset analyzePatternByAI state
            setAnalyzePatternByAI(null);
        }
    }, [analyzePatternByAI.data, analyzePatternByAI.isLoading, analyzePatternByAI.error, showError, showDebug]);

    const GROUP_BY_BANK_AND_CATEGORY = false;
    const handleGroupRowClick = (id: string, date: string, bank: string, category: string, isSimulation?: boolean) => {
        if(viewBy === 'transactions') {
            setSelectedTransactionId(id);
            if (isSimulation) {
                return setOpenModal('sim-detail');
            } else {
                return setOpenModal('detail');
            }
        };

        // Mark this as a drill-down operation
        isDrillDownRef.current = true;

        let start: Date, end: Date;
        let newViewBy: string = viewBy;

        if (date.includes('to')) {
            [start, end] = date.split(' to ').map(d => new Date(d));
        } else if (/^\d{4}$/.test(date)) {
            start = startOfYear(new Date(Number(date), 0));
            end = endOfYear(new Date(Number(date), 0));
        } else {
            start = new Date(date);
            end = new Date(date);
        }

        if(viewBy === 'day') {
            setStartDate(set(start, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 }));
            setEndDate(set(end, { hours: 23, minutes: 59, seconds: 59, milliseconds: 999 }));
            setViewBy('transactions');
            newViewBy = 'transactions';
        } else if(viewBy === 'week') {
            setStartDate(startOfWeek(start, { weekStartsOn: 1 }));
            setEndDate(endOfWeek(end, { weekStartsOn: 1 }));
            setViewBy('day');
            newViewBy = 'day';
        } else if(viewBy === 'month') {
            setStartDate(startOfMonth(start));
            setEndDate(endOfMonth(end));
            setViewBy('week');
            newViewBy = 'week';
        } else if(viewBy === 'quarter') {
            setStartDate(startOfQuarter(start));
            setEndDate(endOfQuarter(end));
            setViewBy('month');
            newViewBy = 'month';
        } else if(viewBy === 'year') {
            setStartDate(startOfYear(start));
            setEndDate(endOfYear(end));
            setViewBy('quarter');
            newViewBy = 'quarter';
        }

        setStartDate(start);
        setEndDate(end);

        if(GROUP_BY_BANK_AND_CATEGORY) {
            setBank(prev => {
                if(bank && bank !== 'unset' && !prev.includes(bank)) {
                    return [...prev, bank];
                }
                return prev;
            });
            setCategory(prev => {
                if(category && category !== 'unset' && !prev.includes(category)) {
                    return [...prev, category];
                }
                return prev;
            });
        }

        // Save immediately for drill-down operations with the new viewBy
        setTimeout(() => {
            saveFiltersImmediately(newViewBy);
            isDrillDownRef.current = false;
        }, 0);
    };

    const handleConfirmMarkTransactions = (transactionGuids: string[], provisionId?: number) => {
        // Filter out any empty or invalid GUIDs
        const validGuids = transactionGuids.filter(guid => guid && guid.trim().length > 0);
        
        if (validGuids.length === 0) {
            showError("No valid transactions selected for linking");
            return;
        }
        
        const targetProvisionId = provisionId || createdProvisionIdRef.current;
        if (!targetProvisionId) {
            showError("Provision ID is missing");
            return;
        }
        
        const { url, method } = buildApiRequestConfig(routes.provisionTemplate.markTransactionAsProvisioned, [{ param: 'id', value: targetProvisionId }]);
        setMarkTrx({ url, method, body: { transactionGuids: validGuids } });
    };

    // Shared by both the debug-mode review window's "Continue" button and the
    // non-debug auto-continue path - takes analysis/source explicitly rather
    // than reading them from component state, since the auto-continue path
    // calls this synchronously inside the analyzePattern response handler,
    // before setAnalysisData/setSourceTransaction from that same response have
    // actually propagated to state yet.
    const proceedToProvisionForm = (guid: string | undefined, analysis: any, source: any) => {
        setSourceTransactionGuid(guid);
        if (Array.isArray(source)) {
            setProvisionThisData({
                date: adjustStartDate(analysis?.startDate, analysis),
                amount: analysis?.amount,
                description: analysis?.simplifiedDescription,
            });
        } else {
            const src = source || {};
            setProvisionThisData({
                date: adjustStartDate(analysis?.startDate || src.date, analysis),
                amount: analysis?.amount || src.amount,
                description: analysis?.simplifiedDescription || src.description,
            });
        }
        setShowAnalysisDialog(false);
        setOpenModal('provision');
    };

    /* SUPERSEDED - bidirectional cursor pagination handlers, no longer used
       now that the server returns the entire filtered set in one request.
       Kept commented out for reference/rollback.

    // "older" direction - scrolling down, further into the past. Continues
    // right after the currently-loaded window's bottom boundary.
    const handleLoadMore = React.useCallback(() => {
        if (isLoadingMore || !hasNextPage || !user?.entityId) return;

        setIsLoadingMore(true);
        setIsInitialLoad(false);
        pendingLoadDirectionRef.current = 'older';

        const processedBankIds = bank.filter(id => !['select-all-banks'].includes(id)).map(id => {
            if (id === 'no-bank-account') {
                return 'null';
            }
            return id;
        });

        const processedCategoryIds = category.filter(id => !['select-all-categories'].includes(id));

        const params = [
            { param: 'entityId', value: user.entityId },
        ];
        const { url, method } = buildApiRequestConfig(routes.transaction.fetchByEntity, params);
        setFetch({ url, method, body: {
            showReal: isReal,
            showRecurring: isRecurringProvisions,
            showOneTime: isOneTimeProvisions,
            bankIds: processedBankIds.length > 0 ? processedBankIds.join(',') : undefined,
            categoryIds: processedCategoryIds.length > 0 ? processedCategoryIds.join(',') : undefined,
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0],
            viewBy: viewBy,
            filterConfig: filterConfig ? JSON.stringify(filterConfig) : undefined,
            simulationId: simulationId?.id || undefined,
            page: currentPage + 1,
            pageSize: pageSize,
            direction: 'older',
            cursorOffset: loadedEndIndex,
            calculateBalance: cashPositionEnabled,
        } });
    }, [isLoadingMore, hasNextPage, currentPage, loadedEndIndex, bank, category, user?.entityId, isReal, isRecurringProvisions, isOneTimeProvisions, startDate, endDate, viewBy, filterConfig, pageSize, simulationId, cashPositionEnabled]);

    // "newer" direction - scrolling up, back toward/past the anchor date.
    // Continues right before the currently-loaded window's top boundary.
    const handleLoadNewer = React.useCallback(() => {
        if (isLoadingNewer || !hasNewer || !user?.entityId) return;

        setIsLoadingNewer(true);
        setIsInitialLoad(false);
        pendingLoadDirectionRef.current = 'newer';

        const processedBankIds = bank.filter(id => !['select-all-banks'].includes(id)).map(id => {
            if (id === 'no-bank-account') {
                return 'null';
            }
            return id;
        });

        const processedCategoryIds = category.filter(id => !['select-all-categories'].includes(id));

        const params = [
            { param: 'entityId', value: user.entityId },
        ];
        const { url, method } = buildApiRequestConfig(routes.transaction.fetchByEntity, params);
        setFetch({ url, method, body: {
            showReal: isReal,
            showRecurring: isRecurringProvisions,
            showOneTime: isOneTimeProvisions,
            bankIds: processedBankIds.length > 0 ? processedBankIds.join(',') : undefined,
            categoryIds: processedCategoryIds.length > 0 ? processedCategoryIds.join(',') : undefined,
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0],
            viewBy: viewBy,
            filterConfig: filterConfig ? JSON.stringify(filterConfig) : undefined,
            simulationId: simulationId?.id || undefined,
            page: 1,
            pageSize: pageSize,
            direction: 'newer',
            cursorOffset: loadedOffset,
            calculateBalance: cashPositionEnabled,
        } });
    }, [isLoadingNewer, hasNewer, loadedOffset, bank, category, user?.entityId, isReal, isRecurringProvisions, isOneTimeProvisions, startDate, endDate, viewBy, filterConfig, pageSize, simulationId, cashPositionEnabled]);
    */

    return (
        <div style={{ width: '100%' }}>
            <Dialog
                open={analyzePattern.isLoading || analyzePatternByAI.isLoading || analyzeMultiple.isLoading || isReanalyzeInProgress}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        textAlign: 'center',
                        padding: isMobile ? 2 : 3,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        margin: isMobile ? 1 : 2
                    }
                }}
            >
                <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                    <CircularProgress sx={{ color: 'var(--color-primary)' }} size={isMobile ? 40 : 50} />
                    <Typography variant={isMobile ? "body1" : "h6"} fontWeight={600}>
                        {analyzeMultiple.isLoading ? 'Analyzing Multiple Transactions' : (analyzePatternByAI.isLoading ? 'AI Analysis In Progress' : (isReanalyzeInProgress ? 'Re-analyzing Transaction Pattern' : 'Analyzing Transaction Pattern'))}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                        {analyzeMultiple.isLoading ? 'Detecting patterns across selected transactions...' : (analyzePatternByAI.isLoading ? 'Using AI to find related transactions...' : (isReanalyzeInProgress ? 'Detecting recurrence pattern using description only...' : 'Detecting recurrence patterns and historical matches...'))}
                    </Typography>
                </Box>
            </Dialog>
            <MountWhenOpened open={showAnalysisDialog}>
                <PatternAnalysisDisplayModal
                    open={showAnalysisDialog}
                    analysisData={analysisData}
                    onClose={() => {
                        setShowAnalysisDialog(false);
                        setProvisionTheseSourceTransactions([]);
                        setProvisionTransactions([]);
                    }}
                    transactionGuid={sourceTransaction?.guid}
                    categories={hierarchicalCategories}
                    isDescriptionOnlyMatch={isDescriptionOnlyMatch}
                    onContinue={(guid) => {
                        // NOTE: transactions get marked later, after the provision template
                        // is actually created - either via the backend's own matching
                        // (createProvisionTemplate + provisionThisSourceGuid) or via the
                        // ProvisionTransactionsDisplay review step. Marking here used to
                        // fire a premature, redundant call against whatever templateId was
                        // left over from a previous provisioning session (createdProvisionId
                        // hadn't been updated for THIS session yet, since the new template
                        // doesn't exist until the form below is submitted) - that's what was
                        // silently mixing unrelated transactions into the wrong template.
                        proceedToProvisionForm(guid, analysisData, sourceTransaction);
                    }}
                    onReanalyze={(guid) => {
                        if (guid) {
                            // prepare for a description-only reanalysis by asking for settings
                            setShowAnalysisDialog(false);
                            setIsReanalyze(true);
                            setIsReanalyzeInProgress(true);
                            reanalyzeCancelledRef.current = false;
                            setPendingDescriptionGuid(guid);
                            setPendingDescriptionText(sourceTransaction?.description || '');
                            setShowMatchSettings(true);
                        }
                    }}
                    onReanalyzeByAI={(guid) => {
                        if (guid) {
                            // Close analysis dialog and start AI reanalysis
                            setShowAnalysisDialog(false);
                            reanalyzeWithAI(guid);
                        }
                    }}
                    showReanalyzeButton={showDebug && !isDescriptionOnlyMatch}
                    showReanalyzeByAIButton={isDescriptionOnlyMatch}
                />
            </MountWhenOpened>
            <MountWhenOpened open={openModal === 'provision'}>
                <AddProvisionPopup
                    open={openModal === 'provision'}
                    onClose={() => {
                        setOpenModal('');
                        setEditingProvisionId(null);
                        setProvisionThisData(null);
                        setAnalysisData(null);
                        setSourceTransactionGuid(undefined);
                        setIsReanalyze(false);
                        setIsReanalyzeInProgress(false);
                        setIsDescriptionOnlyMatch(false);
                        setDescriptionOptions(null);
                        setProvisionTheseSourceTransactions([]);
                        setProvisionTransactions([]);
                    }}
                    onCreate={(newProvision) => {
                        if (newProvision.id) {
                            handleUpdateProvision(newProvision);
                        } else {
                            handleCreateProvision(newProvision);
                        }
                    }}
                    isEdit={{ id: editingProvisionId, state: editingProvisionId !== null }}
                    initialStartDate={provisionThisData?.date}
                    initialAmount={provisionThisData?.amount ? String(provisionThisData.amount) : undefined}
                    initialDescription={provisionThisData?.description}
                    intervalAnalysis={analysisData}
                    sourceTransactionGuid={sourceTransactionGuid || provisionThisData?.sourceTransactionGuid}
                    isLoading={createProvision.isLoading || updateProvision.isLoading}
                />
            </MountWhenOpened>
            <MountWhenOpened open={openAddEntity}>
                <AddEntityPopup
                    open={openAddEntity}
                    isMobile={isMobile}
                    onClose={() => {
                        setOpenAddEntity(false);
                        // Remove the query param from URL
                        setSearchParams(prev => {
                            prev.delete('createCompany');
                            return prev;
                        });
                    }}
                    onConfirm={(data) => {
                        const { url, method } = buildApiRequestConfig(routes.company.create);
                        setCreateCompany({
                            url,
                            method,
                            body: {
                                entityName: data.entityName,
                                countryId: data.country
                            }
                        });
                    }}
                />  
            </MountWhenOpened>

            <MountWhenOpened open={showBasiqImport}>
                <BasiqImportPopup
                    open={showBasiqImport}
                    onClose={() => setShowBasiqImport(false)}
                    onComplete={(data) => {
                        // After Basiq import completes, you can handle the data here
                        showSuccess("Bank transactions imported successfully");
                        setShowBasiqImport(false);
                        // You might want to refresh the transactions or navigate somewhere
                    }}
                    isMobile={isMobile}
                />
            </MountWhenOpened>
            <MountWhenOpened open={openModal === 'transaction'}>
                <AddTransactionModal
                    open={openModal === 'transaction'}
                    isMobile={isMobile}
                    onClose={() => {
                        setOpenModal('');
                        setManualEditTarget(null);
                    }}
                    onCreate={handleSaveTransaction}
                    editTransaction={manualEditInitial}
                />
            </MountWhenOpened>
            <MountWhenOpened open={!!changeDateTarget}>
                <ChangeProvisionDateModal
                    open={!!changeDateTarget}
                    onClose={() => setChangeDateTarget(null)}
                    transaction={changeDateTarget ? { id: changeDateTarget.id, date: changeDateTarget.date, description: changeDateTarget.description } : null}
                    onConfirm={handleConfirmChangeDate}
                    isSaving={updateProvisionDate.isLoading}
                />
            </MountWhenOpened>
            <MountWhenOpened open={openModal === 'import'}>
                <ImportMatchPopup
                    open={openModal === 'import'}
                    isMobile={isMobile}
                    onClose={() => setOpenModal('')}
                    onConfirm={handleConfirmImport}
                    onFileChange={setFileDetails}
                    onBankChange={setImportBankId}
                    fileDetails={fileDetails}
                    bankId={importBankId}
                    // A fully-settled provision (amount already 0) has nothing left
                    // to match a new imported transaction against - leaving it in
                    // the candidate pool let a later import re-match and re-link
                    // it, decrementing an already-zeroed amount further negative.
                    provisions={data?.filter(tx => tx.type === 'provision' && Number(tx.amount) !== 0)?.map(tx => ({
                        id: tx.id,
                        date: tx.date,
                        amount: Number(tx.amount),
                        description: tx.description,
                        originalDescription: tx.originalDescription,
                        category: tx.category,
                    }))}
                    progressModal={progressModal}
                    provisionMatchConfig={companySettings?.settings.provisionMatchConfig}
                />
            </MountWhenOpened>
            <MountWhenOpened open={openModal === 'sim-detail'}>
                <SimulationDetailModal
                    open={openModal === 'sim-detail'}
                    simulationId={selectedTransactionId}
                    onClose={() => {setOpenModal(''); setSelectedTransactionId(null);}}
                />
            </MountWhenOpened>
            <MountWhenOpened open={openModal === 'detail'}>
                {isMobile ? (
                    <TransactionDetailMobile
                        open={openModal === 'detail'}
                        transactionId={selectedTransactionId}
                        invoiceNumber={selectedInvoiceNumber}
                        onClose={() => {setOpenModal(''); setSelectedTransactionId(null); setSelectedInvoiceNumber(null); setAutoOpenLinkModal(false);}}
                        onLink={() => setRefreshTrigger(prev => prev + 1)}
                        autoOpenLinkModal={autoOpenLinkModal}
                        onAutoOpenLinkModalClose={() => setAutoOpenLinkModal(false)}
                        hierarchicalCategories={hierarchicalCategoryOptions}
                        companyStatus={{
                            highestAmount: companyStatus.highestAmount || 9999,
                            lowestAmount: companyStatus.lowestAmount || -9999,
                        }}
                    />
                ) : (
                    <TransactionDetail
                        open={openModal === 'detail'}
                        transactionId={selectedTransactionId}
                        invoiceNumber={selectedInvoiceNumber}
                        onClose={() => {setOpenModal(''); setSelectedTransactionId(null); setSelectedInvoiceNumber(null); setAutoOpenLinkModal(false);}}
                        onLink={() => {
                            // Restores roughly the same scroll position once the
                            // refresh below lands, instead of jumping back to
                            // today - linking/unlinking/closing a transaction
                            // shouldn't feel like a fresh navigation. Coalesced
                            // (see pendingLinkRefreshRef) since "Link and Close
                            // Transaction" fires this multiple times for one
                            // click - only the first call's capture is used, and
                            // only one refetch actually happens.
                            if (pendingLinkRefreshRef.current) return;
                            transactionsTableRef.current?.captureScrollAnchor();
                            pendingLinkRefreshRef.current = setTimeout(() => {
                                pendingLinkRefreshRef.current = null;
                                setRefreshTrigger(prev => prev + 1);
                            }, 500);
                        }}
                        autoOpenLinkModal={autoOpenLinkModal}
                        onAutoOpenLinkModalClose={() => setAutoOpenLinkModal(false)}
                        companyStatus={{
                            highestAmount: companyStatus.highestAmount || 9999,
                            lowestAmount: companyStatus.lowestAmount || -9999,
                        }}
                    />
                )}
            </MountWhenOpened>
            <DeleteModal
                open={showPatternConfirmation}
                message="Continue with Provision?"
                description={patternAnalysisMessage}
                onCancel={() => {
                    setShowPatternConfirmation(false);
                    setPatternAnalysisMessage('');
                    setProvisionThisData(null);
                    setAnalysisData(null);
                    setIsReanalyze(false);
                    setIsReanalyzeInProgress(false);
                    setIsDescriptionOnlyMatch(false);
                }}
                onConfirm={() => {
                    setShowPatternConfirmation(false);
                    setOpenModal('provision');
                }}
            />
            <DeleteModal
                open={showCutoffConfirmDebugOff}
                message="This recurring charge may have stopped"
                description={pendingCutoffConfirmRef.current?.analysis?.cutoffWarning?.reason || 'The pattern looks stale compared to the most recent activity on this account.'}
                confirmLabel="Provision Anyway"
                cancelLabel="Cancel"
                onCancel={() => {
                    setShowCutoffConfirmDebugOff(false);
                    pendingCutoffConfirmRef.current = null;
                    setAnalysisData(null);
                }}
                onConfirm={() => {
                    setShowCutoffConfirmDebugOff(false);
                    const pending = pendingCutoffConfirmRef.current;
                    pendingCutoffConfirmRef.current = null;
                    if (pending) {
                        proceedToProvisionForm(pending.guid, pending.analysis, pending.source);
                    }
                }}
            />
            <DeleteModal
                open={openModal === 'delete'}
                loading={deleteTransaction.isLoading}
                message={isRecurringProvisioning ? ({
                    'this-occurrence': 'Delete This Occurrence?',
                    'this-and-future': 'Delete This and Future Occurrences?',
                    'all-occurrences': 'Delete All Occurrences?',
                } as Record<string, string>)[deleteOption] : "Delete Transaction?"}
                description={isRecurringProvisioning ? ({
                    'this-occurrence': 'This will permanently remove only this occurrence of the recurring provision.',
                    'this-and-future': 'This will permanently remove this occurrence and every future occurrence of the recurring provision.',
                    'all-occurrences': 'This will permanently remove every occurrence of the recurring provision, past and future.',
                } as Record<string, string>)[deleteOption] : "This action will permanently remove the selected transaction."}
                image={{ src: transactionImage, width: 150, height: 150 }}
                onCancel={() => {
                    setOpenModal('');
                    setIsRecurringProvisioning(false);
                    setDeleteOption('this-occurrence');
                    setDeleteIsManual(false);
                }}
                onConfirm={() => {
                    let route = routes.transaction.delete;
                    if (deleteIsManual) {
                        route = routes.transaction.deleteManual;
                    } else if (isRecurringProvisioning) {
                        if (deleteOption === 'this-and-future') {
                            route = routes.provisionTemplate.deleteFutureOccurrences;
                        } else if (deleteOption === 'all-occurrences') {
                            route = routes.provisionTemplate.deleteAllOccurrences;
                        }
                    }
                    const { url, method } = buildApiRequestConfig(route, [{ param: 'guid', value: selectedTransactionId || '' }]);
                    setDeleteTransaction({ url, method });
                    // isRecurringProvisioning/deleteOption are NOT reset here -
                    // the modal stays open (and mounted, still reading these
                    // for its message/description) until the delete request
                    // actually resolves, which can take a moment for
                    // this-and-future/all-occurrences. Resetting them
                    // immediately on click used to flip the still-open
                    // modal's text to the generic "Delete Transaction?" while
                    // the request was still in flight - reset happens once
                    // it truly succeeds instead (see deleteTransaction's
                    // effect above, which also closes the modal).
                }}
            />
            <DateConfirmModal
                open={openModal === 'terminate'}
                message="Terminate Provision?"
                description="This will end the provision on the date you choose. Occurrences after that date will be removed; anything already linked to a real transaction is left untouched."
                confirmLabel="Terminate"
                dateLabel="End Date"
                value={terminateEndDate}
                onChange={setTerminateEndDate}
                confirmDisabled={!terminateEndDate}
                onCancel={() => { setOpenModal(''); setTerminateTarget(null); setTerminateEndDate(''); }}
                onConfirm={() => {
                    if (terminateTarget?.provisionId && terminateEndDate) {
                        const { url, method } = buildApiRequestConfig(routes.provisionTemplate.terminate, [{ param: 'id', value: terminateTarget.provisionId }]);
                        setTerminate({ url, method, body: { endDate: terminateEndDate } });
                    }
                }}
            />
            {isMobile && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, background: 'var(--color-page-background)', borderBottom: '1px solid #e0e0e0', position: 'sticky', top: 0, zIndex: 10, justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Button 
                            variant="contained" 
                            onClick={() => setSidebarOpen(true)} 
                            sx={{ 
                                color: '#fff', 
                                height: 40,
                                display: 'flex',
                                gap: 1,
                                minWidth: 40,
                                p: 1,
                                backgroundColor: 'var(--color-primary)', 
                                '&:hover': { backgroundColor: 'var(--color-primary-hover)' } 
                            }}
                        >
                            <Menu size={20} color="#fff"/>
                        </Button>
                        <Typography sx={{ fontSize: 16, fontWeight: 600, color: 'var(--color-default-text)' }}>Transactions</Typography>
                    </Box>
                    <UserMenu
                        onAccount={() => navigate('/settings/profile')}
                        onLogout={handleLogout}
                    />
                </Box>
            )}
            {!isMobile && (
            <Topbar>
                <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" pr={1}>
                    <Filters
                        status={{
                            cashflowMessage: companyStatus.cashflowMessage || '',
                            state: companyStatus.wentNegative ? 'danger' : 'good',
                            dateInDanger: companyStatus.firstNegativeDate ? new Date(companyStatus.firstNegativeDate) : null,
                            daysBeforeNegative: companyStatus.daysBeforeNegative,
                            disabled: !simulationId && !cashPositionEnabled,
                        }}
                        unitValue={unitValue}
                        setUnitValue={setUnitValue}
                        startDate={startDate}
                        setStartDate={setStartDate}
                        endDate={endDate}
                        setEndDate={setEndDate}
                        isReal={isReal}
                        setIsReal={handleIsRealChange}
                        isRecurringProvisions={isRecurringProvisions}
                        setIsRecurringProvisions={handleIsRecurringProvisionsChange}
                        isOneTimeProvisions={isOneTimeProvisions}
                        setIsOneTimeProvisions={handleIsOneTimeProvisionsChange}
                        bank={bank}
                        setBank={setBank}
                        category={category}
                        setCategory={setCategory}
                        handleCategoryChange={handleCategoryChange}
                        bankOptions={bankTreeOptions}
                        categoryOptions={hierarchicalCategoryOptions}
                        filterConfig={filterConfig}
                        setFilterConfig={setFilterConfig}
                        onFiltersLoaded={() => setFiltersLoaded(true)}
                        onSelectProvision={() => setOpenModal('selectProvision')}
                        appliedSim={simulationId}
                        setAppliedSim={setSimulationId}
                        favoriteSimulations={favoriteSimulations}
                    />
                    <Box display="flex" alignItems="center" gap={0.5}>
                        {showCashPositionToggleFeature && (
                            <Tooltip title="Perf diagnostic: skips the client-side balance/cash-flow calculation entirely when off, to check whether it's contributing to slow loads (doesn't affect an active simulation). Visible because 'Show Cash Position Toggle' is on in /secret-options.">
                                <FormControlLabel
                                    sx={{ mr: 0.5 }}
                                    control={
                                        <Switch
                                            size="small"
                                            checked={cashPositionEnabled}
                                            onChange={(e) => setCashPositionEnabled(e.target.checked)}
                                        />
                                    }
                                    label={<Typography fontSize={12} fontWeight={500}>Cash Position</Typography>}
                                />
                            </Tooltip>
                        )}
                        {isMobile && (
                            <Button 
                                variant="contained" 
                                color="primary" 
                                onClick={() => setSidebarOpen(true)} 
                                sx={{ 
                                    color: '#fff', 
                                    height: 40,
                                    display: 'flex',
                                    gap: 1,
                                    minWidth: 40,
                                    p: 1,
                                    backgroundColor: 'var(--color-primary)', 
                                    '&:hover': { backgroundColor: 'var(--color-primary)' } 
                                }}
                            >
                                <Menu size={20} color="#fff"/>
                            </Button>
                        )}
                        {!isMobile && (
                            <XeroSyncButton type="transactions" compact />
                        )}
                        <Tooltip title="Today">
                            <span>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => transactionsTableRef.current?.scrollToToday()}
                                    sx={{
                                        color: 'var(--color-primary)',
                                        height: 40,
                                        minWidth: 40,
                                        px: 1.25,
                                        display: 'flex',
                                        alignItems: 'center',
                                        overflow: 'hidden',
                                        whiteSpace: 'nowrap',
                                        border: '1px solid var(--color-primary)',
                                        backgroundColor: 'var(--color-page-background)',
                                        '&:hover': { backgroundColor: 'rgba(var(--color-primary-rgb), 0.0863)' },
                                    }}
                                >
                                    <Calendar size={16} color="var(--color-primary)"/>
                                </Button>
                            </span>
                        </Tooltip>
                        <Tooltip title="Import statement">
                            <span>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    disabled={fetchCategories.isLoading || fetchBanks.isLoading || !can('transactions.import', 'create')}
                                    onClick={() => { setOpenModal('import'); setDoneImporting(false); }}
                                    sx={{
                                        color: 'var(--color-primary)',
                                        height: 40,
                                        minWidth: 40,
                                        px: 1.25,
                                        display: 'flex',
                                        alignItems: 'center',
                                        overflow: 'hidden',
                                        whiteSpace: 'nowrap',
                                        border: '1px solid var(--color-primary)',
                                        backgroundColor: 'var(--color-page-background)',
                                        '&:hover': { backgroundColor: 'rgba(var(--color-primary-rgb), 0.0863)' },
                                        '&:disabled': { opacity: 0.5, backgroundColor: 'var(--color-page-background)' }
                                    }}
                                >
                                    <Upload size={16} color="var(--color-primary)"/>
                                </Button>
                            </span>
                        </Tooltip>
                        <Tooltip title="Add provision">
                            <span>
                                <Button
                                    disabled={!can('transactions.addProvision', 'create')}
                                    onClick={() => {
                                        setIsDescriptionOnlyMatch(false);
                                        setOpenModal('provision');
                                    }}
                                    sx={{
                                        color: '#fff',
                                        height: 40,
                                        minWidth: 40,
                                        px: 1.25,
                                        display: 'flex',
                                        alignItems: 'center',
                                        overflow: 'hidden',
                                        whiteSpace: 'nowrap',
                                        backgroundColor: 'var(--color-primary)',
                                        '&:hover': { backgroundColor: 'var(--color-primary)' },
                                        '&:disabled': { opacity: 0.5 }
                                    }}
                                >
                                    <Box sx={{
                                        width: 20,
                                        height: 20,
                                        borderRadius: '50%',
                                        background: 'var(--color-page-background)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0 }}
                                    >
                                        <Plus size={16} color="var(--color-primary)"/>
                                    </Box>
                                </Button>
                            </span>
                        </Tooltip>
                        <Tooltip title="Add transaction">
                            <span>
                                <Button
                                    disabled={!canCreateTransactions}
                                    onClick={() => setOpenModal('transaction')}
                                    sx={{
                                        color: '#fff',
                                        height: 40,
                                        minWidth: 40,
                                        px: 1.25,
                                        display: 'flex',
                                        alignItems: 'center',
                                        overflow: 'hidden',
                                        whiteSpace: 'nowrap',
                                        backgroundColor: 'var(--color-primary)',
                                        '&:hover': { backgroundColor: 'var(--color-primary)' },
                                        '&:disabled': { opacity: 0.5 }
                                    }}
                                >
                                    <Receipt size={20} color="#fff"/>
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>
                </Box>
            </Topbar>
            )}
            {isMobile && (
                <FiltersMobile
                    status={{
                        cashflowMessage: companyStatus.cashflowMessage || '',
                        state: companyStatus.wentNegative ? 'danger' : 'good',
                        dateInDanger: companyStatus.firstNegativeDate ? new Date(companyStatus.firstNegativeDate) : null,
                        daysBeforeNegative: companyStatus.daysBeforeNegative,
                        disabled: !simulationId && !cashPositionEnabled,
                    }}
                    unitValue={unitValue}
                    setUnitValue={setUnitValue}
                    startDate={startDate}
                    setStartDate={setStartDate}
                    endDate={endDate}
                    setEndDate={setEndDate}
                    viewBy={viewBy}
                    setViewBy={setViewBy}
                    isDateRange={isDateRange}
                    setIsDateRange={setIsDateRange}
                    companyStatus={companyStatus}
                    isReal={isReal}
                    setIsReal={handleIsRealChange}
                    isRecurringProvisions={isRecurringProvisions}
                    setIsRecurringProvisions={handleIsRecurringProvisionsChange}
                    isOneTimeProvisions={isOneTimeProvisions}
                    setIsOneTimeProvisions={handleIsOneTimeProvisionsChange}
                    bank={bank}
                    setBank={setBank}
                    category={category}
                    setCategory={setCategory}
                    handleCategoryChange={handleCategoryChange}
                    bankOptions={bankTreeOptions}
                    categoryOptions={hierarchicalCategoryOptions}
                    filterConfig={filterConfig}
                    setFilterConfig={setFilterConfig}
                    onFiltersLoaded={() => setFiltersLoaded(true)}
                    appliedSim={simulationId}
                    setAppliedSim={setSimulationId}
                    onSelectProvision={() => setOpenModal('selectProvision')}
                    favoriteSimulations={favoriteSimulations}
                    compactView={compactView}
                    setCompactView={setCompactView}
                    onScrollToToday={() => transactionsTableMobileRef.current?.scrollToToday()}
                    onImport={() => { setOpenModal('import'); setDoneImporting(false); }}
                    onAddProvision={() => { setIsDescriptionOnlyMatch(false); setOpenModal('provision'); }}
                    onAddTransaction={() => setOpenModal('transaction')}
                    canImport={!fetchCategories.isLoading && !fetchBanks.isLoading && can('transactions.import', 'create')}
                    canAddProvision={!fetchCategories.isLoading && !fetchBanks.isLoading && can('transactions.addProvision', 'create')}
                    canAddTransaction={!fetchCategories.isLoading && !fetchBanks.isLoading && canCreateTransactions}
                />
            )}
            <div style={{ padding: '10px' }}>
                {isMobile ? (
                    // manual timing for mobile table handled via render hooks
                    <>
                        <TransactionsTableMobile
                            ref={transactionsTableMobileRef}
                            data={data}
                            bankOptions={banks}
                            categoryOptions={flatCategoryDropdownOptions}
                            onDelete={(row, deleteScope) => {
                                setSelectedTransactionId(row.id);
                                // Real transactions only ever offer Delete when manually added
                                // (see isManual) - those go through a dedicated route, since
                                // the generic one is provision-only.
                                setDeleteIsManual(row.type === 'real' && row.isManual === true);
                                // deleteScope is only ever passed by the ActionMenu's
                                // recurring-provision submenu (see
                                // transactions-table-virtual.tsx/transactions-table-mobile.tsx) -
                                // its presence alone is the authoritative signal, so trust
                                // it instead of re-deriving from row fields a second time.
                                setIsRecurringProvisioning(!!deleteScope);
                                if (deleteScope) setDeleteOption(deleteScope);
                                setOpenModal('delete');
                            }}
                            onTerminate={(row) => {
                                setTerminateTarget(row);
                                setTerminateEndDate(dayjs().format('YYYY-MM-DD'));
                                setOpenModal('terminate');
                            }}
                            onEdit={(row) => {
                                if (row.type === 'real' && row.isManual) {
                                    // Manually-added transaction - reuse the add form in edit mode.
                                    setManualEditTarget(row);
                                    setOpenModal('transaction');
                                    return;
                                }
                                if (row.invoiceNumber) {
                                    navigate('/invoices');
                                } else if (row.staffPaymentId) {
                                    navigate(`/staff?editStaffId=${row.staffPaymentId}`);
                                } else if (row.provisionId) {
                                    setEditingProvisionId(row.provisionId);
                                    setIsDescriptionOnlyMatch(false);
                                    setOpenModal('provision');
                                }
                            }}
                            onLinkProvision={(row) => {
                                setSelectedTransactionId(row.id);
                                setAutoOpenLinkModal(true);
                                setOpenModal('detail');
                            }}
                            onChangeDate={(row) => setChangeDateTarget(row)}
                            onMarkTransactions={(transactionGuids, provisionId) => {
                                if (provisionId) {
                                    handleConfirmMarkTransactions(transactionGuids, provisionId);
                                } else {
                                    showError('Please select a provision');
                                }
                            }}
                            onProvisionThese={handleProvisionThese}
                            onProvisionThis={(row) => {
                                const { url, method } = buildApiRequestConfig(routes.provisionTemplate.analyzePattern, [{ param: 'transactionGuid', value: row.id }]);
                                setIsReanalyze(false);
                                setIsDescriptionOnlyMatch(false);
                                setAnalyzePattern({ url, method });
                                setProvisionThisData({
                                    date: adjustStartDate(row.date),
                                    amount: row.amount,
                                    description: row.description
                                });
                            }}
                            onTaxChange={handleUpdateTransactionTax}
                            taxOptions={taxOptions}
                            companyStatus={companyStatus}
                            stage={initialLoadComplete ? stage : 'initialize'}
                            viewBy={viewBy}
                            setViewBy={setViewBy}
                            unitValue={unitValue}
                            setUnitValue={setUnitValue}
                            isDateRange={isDateRange}
                            setIsDateRange={setIsDateRange}
                            setStartDate={setStartDate}
                            setEndDate={setEndDate}
                            onRowClick={handleGroupRowClick}
                            setColumnFilter={setFilterConfig}
                            columnFilter={filterConfig}
                            canUpdate={canUpdateTransactions}
                            canDelete={canDeleteTransactions}
                            compactView={compactView}
                        />
                    </>
                ) : (
                    // regular table (timing hooks still run)
                    <>
                        <TransactionsTable
                            ref={transactionsTableRef}
                            data={data}
                            columns={columns} 
                            onDelete={(row, deleteScope) => {
                                setSelectedTransactionId(row.id);
                                // Real transactions only ever offer Delete when manually added
                                // (see isManual) - those go through a dedicated route, since
                                // the generic one is provision-only.
                                setDeleteIsManual(row.type === 'real' && row.isManual === true);
                                // deleteScope is only ever passed by the ActionMenu's
                                // recurring-provision submenu (see
                                // transactions-table-virtual.tsx/transactions-table-mobile.tsx) -
                                // its presence alone is the authoritative signal, so trust
                                // it instead of re-deriving from row fields a second time.
                                setIsRecurringProvisioning(!!deleteScope);
                                if (deleteScope) setDeleteOption(deleteScope);
                                setOpenModal('delete');
                            }}
                            onTerminate={(row) => {
                                setTerminateTarget(row);
                                setTerminateEndDate(dayjs().format('YYYY-MM-DD'));
                                setOpenModal('terminate');
                            }}
                            onEdit={(row) => {
                                if (row.type === 'real' && row.isManual) {
                                    // Manually-added transaction - reuse the add form in edit mode.
                                    setManualEditTarget(row);
                                    setOpenModal('transaction');
                                    return;
                                }
                                if (row.invoiceNumber) {
                                    navigate('/invoices');
                                } else if (row.staffPaymentId) {
                                    navigate(`/staff?editStaffId=${row.staffPaymentId}`);
                                } else if (row.provisionId) {
                                    setEditingProvisionId(row.provisionId);
                                    setIsDescriptionOnlyMatch(false);
                                    setOpenModal('provision');
                                }
                            }}
                            onLinkProvision={(row) => {
                                setSelectedTransactionId(row.id);
                                setAutoOpenLinkModal(true);
                                setOpenModal('detail');
                            }}
                            onEditProvisionTemplate={(templateId) => {
                                setEditingProvisionId(templateId);
                                setIsDescriptionOnlyMatch(false);
                                setOpenModal('provision');
                            }}
                            onProvisionThis={(row) => {
                                const { url, method } = buildApiRequestConfig(routes.provisionTemplate.analyzePattern, [{ param: 'transactionGuid', value: row.id }]);
                                setAnalyzePattern({ url, method });
                                setIsReanalyze(false);
                                setIsDescriptionOnlyMatch(false);

                                setProvisionThisData({
                                    date: adjustStartDate(row.date),
                                    amount: row.amount,
                                    description: row.description
                                });
                            }}
                            onMarkTransactions={(transactionGuids, provisionId) => {
                                if (provisionId) {
                                    handleConfirmMarkTransactions(transactionGuids, provisionId);
                                } else {
                                    showError('Please select a provision');
                                }
                            }}
                            onProvisionThese={handleProvisionThese}
                            onCategoryChange={handleUpdateTransactionCategory}
                            onTaxChange={handleUpdateTransactionTax}
                            onChangeDate={(row) => setChangeDateTarget(row)}
                            taxOptions={taxOptions}
                            setColumnFilter={handleFilterConfigChange}
                            columnFilter={filterConfig}
                            bankOptions={banks}
                            categoryOptions={flatCategoryDropdownOptions}
                            hierarchicalCategories={hierarchicalCategoryOptions}
                            categoryDefaultTaxIds={categoryDefaultTaxIdsMap}
                            onRowClick={handleGroupRowClick}
                            isHeaderFilterUsed={!!bank.length || !!category.length || !isReal || (!isRecurringProvisions && !isOneTimeProvisions)}
                            startDate={startDate}
                            endDate={endDate}
                            viewBy={viewBy}
                            setViewBy={setViewBy}
                            unitValue={unitValue}
                            setUnitValue={setUnitValue}
                            isDateRange={isDateRange}
                            setIsDateRange={setIsDateRange}
                            setStartDate={setStartDate}
                            setEndDate={setEndDate}
                            companyStatus={companyStatus}
                            stage={initialLoadComplete ? stage : 'initialize'}
                            canUpdate={canUpdateTransactions}
                            canDelete={canDeleteTransactions}
                            canUpdateProvisions={canUpdateProvisions}
                            canDeleteProvisions={canDeleteProvisions}
                            canCreateProvisions={canCreateProvisions}
                        />
                    </>
                )}
            </div>
            <SidebarMobileDrawer
                open={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
            />
            <MountWhenOpened open={showMatchSettings}>
                <DescriptionMatchSettingsModal
                    open={showMatchSettings}
                    sourceDescription={pendingDescriptionText}
                    onClose={() => {
                        setShowMatchSettings(false);
                        setIsReanalyzeInProgress(false);
                        setIsReanalyze(false);
                        setPendingDescriptionGuid(null);
                    }}
                    onConfirm={(start, length) => {
                        setShowMatchSettings(false);
                        setDescriptionOptions({ start, length });
                        setIsDescriptionOnlyMatch(true);
                        if (pendingDescriptionGuid) {
                            reanalyzeWithDescriptionOnly(pendingDescriptionGuid, start, length);
                        }
                    }}
                />
            </MountWhenOpened>

            <MountWhenOpened open={openModal === 'selectProvision'}>
                <SimulationsPopup
                    open={openModal === 'selectProvision'}
                    onClose={() => {
                        setOpenModal('');
                    }}
                    onSimulationSelect={(simulationId) => setSimulationId(simulationId)}
                    onFavoritesUpdated={(updatedFavorites) => {
                        // Replace favorites with the updated list from the modal
                        setFavoriteSimulations(updatedFavorites);
                    }}
                    onSimulationsUpdated={() => {
                        // Refresh simulations list when modal updates a simulation
                        if (user?.entityId) {
                            const { url, method } = buildApiRequestConfig(routes.simulation.fetchByEntity, [{ param: 'entityId', value: user.entityId }]);
                            setFetchSimulations({ url, method });
                        }
                    }}
                    onSimulationDeleted={(deletedSimulationId) => {
                        // Close the simulation detail modal if the deleted simulation is being viewed
                        if (selectedTransactionId === deletedSimulationId && openModal === 'sim-detail') {
                            setOpenModal('');
                            setSelectedTransactionId(null);
                        }
                        // Clear the applied simulation if it matches the deleted one
                        if (simulationId?.id === deletedSimulationId) {
                            setSimulationId(null);
                        }
                    }}
                    isReal={isReal}
                    isRecurringProvisions={isRecurringProvisions}
                    isOneTimeProvisions={isOneTimeProvisions}
                    appliedSimulationId={simulationId?.id || null}
                />
            </MountWhenOpened>
            <MountWhenOpened open={showProvisionTransactions}>
                <ProvisionTransactionsDisplayModal
                    open={showProvisionTransactions}
                    onConfirm={(trxGuids: string[]) => {
                        handleConfirmMarkTransactions(trxGuids);
                        // Don't close modal here - it will close in the markTrx effect when API succeeds
                    }}
                    onClose={() => {
                        setShowProvisionTransactions(false);
                        setCreatedProvisionIdBoth(null);
                        setProvisionTransactions([]);
                        setProvisionTheseSourceTransactions([]);
                    }}
                    provisionId={createdProvisionId}
                    transactions={provisionTheseSourceTransactions.length > 0 ? provisionTheseSourceTransactions : provisionTransactions}
                    banks={rawBanks}
                    categories={hierarchicalCategories}
                    isLoading={markTrx.isLoading}
                />
            </MountWhenOpened>
        </div>
    );
};




export default TransactionsVirtual;